// ========================================
// 闇属性カードの固有効果
// ========================================

import {
  getPlayerContext,
  millOpponentDeck,
  conditionalDamage,
  searchCard,
  reviveFromGraveyard,
  modifyAttack,
  drawCards,
  selectAndApplyStatusToOpponent,
  applyStatusToOpponentMonster,
  applyStatusToAllOpponentMonsters,
} from '../effectHelpers';
import { STATUS_EFFECT_TYPES, statusEffectEngine } from '../statusEffects';
import type { CardEffectRegistry } from './index';
import type { MonsterInstance, CardWithId, CardData, PlayerNumber } from '../../types';

/**
 * 闘属性カードの固有効果
 */
export const darkCardEffects: CardEffectRegistry = {
  /**
   * C0000078: 禁忌の傀儡師マレウス
   * 基本技：墓地の闇属性モンスター1体を自分の場に戻す（攻撃力300、HP800、効果無効）
   */
  C0000078: (skillText, context) => {
    const { addLog, summoningSickness = true } = context;
    const { myGraveyard, setMyGraveyard, myField, setMyField, currentPlayer } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const darkMonster = myGraveyard.find(card =>
        card.type === 'monster' && card.attribute === '闇'
      );

      if (!darkMonster) {
        addLog('墓地に闇属性モンスターがありません', 'info');
        return false;
      }

      const emptySlotIndex = myField.findIndex(slot => slot === null);
      if (emptySlotIndex === -1) {
        addLog('場が満杯です', 'info');
        return false;
      }

      // 墓地から蘇生（攻撃力300、HP800固定）
      setMyGraveyard(prev => prev.filter(c => c.uniqueId !== darkMonster.uniqueId));
      setMyField(prev => {
        const newField = [...prev];
        newField[emptySlotIndex] = {
          ...darkMonster,
          attack: 300,
          currentAttack: 300,
          hp: 800,
          currentHp: 800,
          canAttack: !summoningSickness, // 召喚酔い設定に依存
          owner: currentPlayer,
          charges: [],
          statusEffects: [],
        };
        return newField;
      });

      addLog(`${darkMonster.name}を蘇生（攻撃力300、HP800）`, 'info');
      return true;
    }
    return false;
  },

  /**
   * C0000094: 闇鴉の斥候
   * 【召喚時】相手のデッキの上から1枚を墓地に送る
   */
  C0000094: (skillText, context) => {
    if (skillText.includes('【召喚時】')) {
      millOpponentDeck(context, 1);
      return true;
    }
    return false;
  },

  /**
   * C0000096: 奈落の追跡者
   * 【召喚時】相手の手札を1枚選び、それがモンスターなら墓地に送る
   */
  C0000096: (skillText, context) => {
    const { addLog } = context;
    const { opponentHand, setOpponentHand, setOpponentGraveyard } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      if (opponentHand.length === 0) {
        addLog('相手の手札がありません', 'info');
        return true;
      }

      // ランダムに1枚選択
      const randomIndex = Math.floor(Math.random() * opponentHand.length);
      const selectedCard = opponentHand[randomIndex];

      if (selectedCard.type === 'monster') {
        setOpponentHand(prev => prev.filter((_, idx) => idx !== randomIndex));
        setOpponentGraveyard(prev => [...prev, selectedCard]);
        addLog(`相手の手札「${selectedCard.name}」を墓地に送った`, 'damage');
      } else {
        addLog(`選ばれたカードはモンスターではありませんでした`, 'info');
      }
      return true;
    }
    return false;
  },

  /**
   * C0000099: 虚蝕獣・爪刃の群れ
   * 【召喚時】相手モンスター1体に800ダメージを与える
   */
  C0000099: (skillText, context) => {
    if (skillText.includes('【召喚時】')) {
      return conditionalDamage(context, 800, 'opponent_monster', 0);
    }
    return false;
  },

  /**
   * C0000113: 咆哮の虚蝕獣
   * 【召喚時】相手のデッキの上から3枚を墓地に送る
   * 基本技：相手モンスター1体に500ダメージを与える
   */
  C0000113: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 召喚時効果（トリガーでも処理）
    if (skillText.includes('【召喚時】')) {
      millOpponentDeck(context, 3);
      return true;
    }

    // 基本技：相手モンスター1体に500ダメージ
    if (context.skillType === 'basic') {
      const validTargets = opponentField
        .map((m, i) => ({ monster: m, index: i }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('咆哮の虚蝕獣: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      setPendingMonsterTarget({
        message: '500ダメージを与える相手モンスターを選択',
        validTargets: validTargets.map(t => t.index),
        targetPlayer: 'opponent',
        callback: (targetIndex: number) => {
          setOpponentField(prev => {
            const newField = [...prev];
            const target = newField[targetIndex];
            if (!target) return newField;

            const newHp = (target.currentHp ?? target.hp ?? 0) - 500;
            if (newHp <= 0) {
              addLog(`咆哮の虚蝕獣の基本技: ${target.name}に500ダメージ！破壊した！`, 'damage');
              newField[targetIndex] = null;
              setOpponentGraveyard(prev => [...prev, target]);
            } else {
              newField[targetIndex] = {
                ...target,
                currentHp: newHp,
              };
              addLog(`咆哮の虚蝕獣の基本技: ${target.name}に500ダメージ！（残りHP: ${newHp}）`, 'damage');
            }
            return newField;
          });
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    return false;
  },

  /**
   * C0000115: 禁断の使徒
   * 【召喚時】相手モンスター1体に300ダメージを与える
   */
  C0000115: (skillText, context) => {
    if (skillText.includes('【召喚時】')) {
      return conditionalDamage(context, 300, 'opponent_monster', 0);
    }
    return false;
  },

  /**
   * C0000126: ダーク・シャンデリア
   * 【召喚時】相手モンスター全体の攻撃力を300ダウン
   */
  C0000126: (skillText, context) => {
    const { addLog } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      const monsters = opponentField.filter(m => m !== null);
      if (monsters.length === 0) {
        addLog('相手モンスターがいません', 'info');
        return true;
      }

      setOpponentField(prev => prev.map(m => {
        if (m) {
          const newAtk = Math.max(0, (m.attack ?? 0) - 300);
          return { ...m, attack: newAtk };
        }
        return m;
      }));

      addLog('相手モンスター全体の攻撃力を300ダウン', 'info');
      return true;
    }
    return false;
  },

  /**
   * C0000231: 幼魔王女リリカ
   * 【召喚時】デッキから《ゴシック》または《鎖》魔法カード1枚を手札に加える
   * 基本技：自身の攻撃力の半分のダメージを相手モンスター1体に与え、その攻撃力を500ダウン
   */
  C0000231: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      return searchCard(context, (card) => {
        return card.type === 'magic' &&
               !!card.name && (card.name.includes('ゴシック') || card.name.includes('鎖'));
      }) !== null;
    }

    // 基本技：攻撃力半分ダメージ+500ダウン
    if (context.skillType === 'basic') {
      const thisCard = myField[monsterIndex];
      if (!thisCard) {
        addLog('対象のモンスターが見つかりません', 'info');
        return false;
      }

      const damage = Math.floor(thisCard.currentAttack / 2);

      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return false;
      }

      setPendingMonsterTarget({
        message: `ダメージ（${damage}）を与えて攻撃力を500下げるモンスターを選択`,
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          setOpponentField(prev => {
            const newField = [...prev];
            if (newField[selectedIndex]) {
              const target = newField[selectedIndex];
              const newHp = target.currentHp - damage;

              if (newHp <= 0) {
                // 破壊
                setOpponentGraveyard(prevGrave => [...prevGrave, target]);
                newField[selectedIndex] = null;
                addLog(`${target.name}に${damage}ダメージを与えて破壊した`, 'damage');
              } else {
                newField[selectedIndex] = {
                  ...target,
                  currentHp: newHp,
                  currentAttack: Math.max(0, target.currentAttack - 500),
                };
                addLog(`${target.name}に${damage}ダメージを与え、攻撃力を500下げた（残りHP: ${newHp}、ATK: ${newField[selectedIndex].currentAttack}）`, 'damage');
              }
            }
            return newField;
          });
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }
    return false;
  },

  /**
   * C0000232: 奉仕のリリカ
   * 【召喚時】自分のSPトークンを１つアクティブにする
   * 基本技：相手モンスター1体の効果をターン終了時まで無効化
   */
  C0000232: (skillText, context) => {
    const { addLog } = context;
    const { myRestedSP, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      if (myRestedSP > 0) {
        setMyActiveSP(prev => prev + 1);
        setMyRestedSP(prev => prev - 1);
        addLog('レストSPを1個アクティブにした', 'info');
        return true;
      } else {
        addLog('レストSPがありません', 'info');
        return false;
      }
    }

    // 基本技：相手モンスターの効果無効化（SILENCE状態異常を使用）
    if (context.skillType === 'basic') {
      return selectAndApplyStatusToOpponent(context, STATUS_EFFECT_TYPES.SILENCE, {
        duration: 0,  // このターン終了時まで
      }, '奉仕のリリカ');
    }
    return false;
  },

  /**
   * C0000384: 魔女エリザヴェット・ヴェイル
   * 基本技: 自分の墓地の《黒呪》魔法カード1枚を手札に戻す（ターンに1度）
   */
  C0000384: (skillText, context) => {
    const { addLog, setPendingGraveyardSelection } = context;
    const { myGraveyard, setMyGraveyard, setMyHand } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 墓地から《黒呪》魔法カードを検索
      const blackCurseMagics = myGraveyard.filter(
        (card) => card.type === 'magic' && !!card.name && card.name.includes('黒呪')
      );

      if (blackCurseMagics.length === 0) {
        addLog('墓地に《黒呪》魔法カードがありません', 'info');
        return false;
      }

      // 複数ある場合は選択UIを表示
      if (blackCurseMagics.length > 1 && setPendingGraveyardSelection) {
        setPendingGraveyardSelection({
          message: '手札に戻す《黒呪》魔法カードを選択',
          cards: blackCurseMagics,
          callback: (selectedCards: CardWithId[] | CardWithId | null) => {
            const targetCard = Array.isArray(selectedCards) ? selectedCards[0] : selectedCards;
            if (targetCard) {
              // 墓地から除去
              setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== targetCard.uniqueId));
              // 手札に追加
              setMyHand((prev) => [...prev, targetCard]);
              addLog(`${targetCard.name}を手札に戻した`, 'info');
            }
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // 1枚だけの場合は自動選択
      const targetCard = blackCurseMagics[0];
      setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== targetCard.uniqueId));
      setMyHand((prev) => [...prev, targetCard]);
      addLog(`${targetCard.name}を手札に戻した`, 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000424: 呪灰の翼ダスクドラゴン
   * 基本技: 自分のライフを600減らし、相手モンスター1体のHPを1200減らす（ターンに1度）
   */
  C0000424: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { setMyLife, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手モンスターがいるか確認
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true; // 対象がいなくても技の発動自体は成功として扱う
      }

      // ライフが足りるか確認（0以下にはなれるが、自滅するかどうかはゲームルール次第）
      // ライフコストとして600減らす
      setMyLife((prev) => prev - 600);
      addLog('呪灰の翼ダスクドラゴンの基本技！ライフを600支払った', 'damage');

      // 1体だけの場合は自動選択
      if (opponentMonsters.length === 1) {
        const target = opponentMonsters[0];
        const monster = target.monster;
        if (!monster) return false;
        const newHp = Math.max(0, (monster.currentHp ?? monster.hp ?? 0) - 1200);
        addLog(`${monster.name}のHPを1200減少！（残りHP: ${newHp}）`, 'damage');

        if (newHp <= 0) {
          // モンスター破壊
          setOpponentField((prev) => {
            const newField = [...prev];
            newField[target.index] = null;
            return newField;
          });
          setOpponentGraveyard((prev) => [...prev, monster as unknown as CardWithId]);
          addLog(`${monster.name}は破壊された！`, 'damage');
        } else {
          setOpponentField((prev) =>
            prev.map((m, idx) => {
              if (idx === target.index && m) {
                return { ...m, currentHp: newHp };
              }
              return m;
            })
          );
        }
        return true;
      }

      // 複数モンスターがいる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: 'HPを1200減らすモンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            const selectedMonster = opponentField[selectedIndex];
            if (selectedMonster) {
              const newHp = Math.max(0, selectedMonster.currentHp - 1200);
              addLog(`${selectedMonster.name}のHPを1200減少！（残りHP: ${newHp}）`, 'damage');

              if (newHp <= 0) {
                // モンスター破壊
                setOpponentField((prev) => {
                  const newField = [...prev];
                  newField[selectedIndex] = null;
                  return newField;
                });
                setOpponentGraveyard((prev) => [...prev, selectedMonster]);
                addLog(`${selectedMonster.name}は破壊された！`, 'damage');
              } else {
                setOpponentField((prev) =>
                  prev.map((m, idx) => {
                    if (idx === selectedIndex && m) {
                      return { ...m, currentHp: newHp };
                    }
                    return m;
                  })
                );
              }
            }
          },
          selectionOwner: context.currentPlayer,
        });
      }
      return true;
    }

    return false;
  },

  /**
   * C0000396: 呪骸王エリザルヴェドン
   * 基本技: 自分の墓地の《黒呪》魔法カード1枚をデッキに戻し、このカードの攻撃力をターン終了時まで800アップ（ターンに1度）
   * 上級技: 自分の墓地の《黒呪》魔法カード1枚をデッキに戻し、そのカードの効果を発動する
   */
  C0000396: (skillText, context) => {
    const { addLog, monsterIndex, setPendingGraveyardSelection } = context;
    const { myGraveyard, setMyGraveyard, setMyDeck, setMyField } = getPlayerContext(context);

    // 墓地から《黒呪》魔法カードを検索
    const blackCurseMagics = myGraveyard.filter(
      (card) => card.type === 'magic' && !!card.name && card.name.includes('黒呪')
    );

    if (blackCurseMagics.length === 0) {
      addLog('墓地に《黒呪》魔法カードがありません', 'info');
      return false;
    }

    // 基本技: 墓地の黒呪魔法1枚をデッキに戻し、攻撃力800アップ
    if (context.skillType === 'basic') {
      const executeBasicEffect = (selectedCard: CardWithId) => {
        // 墓地から除去
        setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== selectedCard.uniqueId));
        // デッキに戻す（シャッフル）
        setMyDeck((prev) => {
          const newDeck = [...prev, selectedCard];
          // シャッフル
          for (let i = newDeck.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
          }
          return newDeck;
        });
        addLog(`${selectedCard.name}をデッキに戻した`, 'info');

        // 攻撃力800アップ
        if (monsterIndex !== undefined && monsterIndex !== null) {
          setMyField((prev) =>
            prev.map((m, idx) => {
              if (idx === monsterIndex && m) {
                const newAtk = m.currentAttack + 800;
                return { ...m, currentAttack: newAtk };
              }
              return m;
            })
          );
          addLog('呪骸王エリザルヴェドンの攻撃力が800アップ！（ターン終了時まで）', 'info');
        }
      };

      // 複数ある場合は選択UI
      if (blackCurseMagics.length > 1 && setPendingGraveyardSelection) {
        setPendingGraveyardSelection({
          message: 'デッキに戻す《黒呪》魔法カードを選択',
          cards: blackCurseMagics,
          callback: (selectedCards: CardWithId[]) => {
            if (selectedCards[0]) executeBasicEffect(selectedCards[0]);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // 1枚だけの場合は自動選択
      executeBasicEffect(blackCurseMagics[0]);
      return true;
    }

    // 上級技: 墓地の黒呪魔法1枚をデッキに戻し、そのカードの効果を発動
    if (context.skillType === 'advanced') {
      const { executeSkillEffects } = require('../effectEngine');

      const executeAdvancedEffect = (selectedCard: CardWithId) => {
        // 墓地から除去
        setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== selectedCard.uniqueId));
        // デッキに戻す（シャッフル）
        setMyDeck((prev) => {
          const newDeck = [...prev, selectedCard];
          // シャッフル
          for (let i = newDeck.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
          }
          return newDeck;
        });
        addLog(`${selectedCard.name}をデッキに戻した`, 'info');
        addLog(`${selectedCard.name}の効果を発動！`, 'info');

        // 選択したカードの効果を発動
        const effectText = selectedCard.effect || '';
        executeSkillEffects(effectText, context, selectedCard.id);
      };

      // 複数ある場合は選択UI
      if (blackCurseMagics.length > 1 && setPendingGraveyardSelection) {
        setPendingGraveyardSelection({
          message: '効果を発動する《黒呪》魔法カードを選択',
          cards: blackCurseMagics,
          callback: (selectedCards: CardWithId[]) => {
            if (selectedCards[0]) executeAdvancedEffect(selectedCards[0]);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // 1枚だけの場合は自動選択
      executeAdvancedEffect(blackCurseMagics[0]);
      return true;
    }

    return false;
  },

  /**
   * C0000390: 黒呪・ヴェルザクトの黒刃
   * 相手モンスター1体に1200ダメージを与え、次のターン終了時までそのモンスターの攻撃力を500下げる。
   * 【刹那詠唱】対応（SPトークン1個を追加でレスト状態にすることで相手ターンでも発動可能）
   */
  C0000390: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // ステータス効果エンジンをインポート
    const { statusEffectEngine, STATUS_EFFECT_TYPES } = require('../statusEffects');

    // 相手モンスターがいるか確認
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('相手の場にモンスターがいません', 'info');
      return false;
    }

    // ダメージと効果を適用する関数
    const applyEffect = (targetIndex: number) => {
      const targetMonster = opponentField[targetIndex];
      if (!targetMonster) return;

      const damage = 1200;
      const newHp = targetMonster.currentHp - damage;

      addLog(`黒呪・ヴェルザクトの黒刃: ${targetMonster.name}に${damage}ダメージ！`, 'damage');

      if (newHp <= 0) {
        // モンスター破壊
        setOpponentField(prev => {
          const newField = [...prev];
          newField[targetIndex] = null;
          return newField;
        });
        setOpponentGraveyard(prev => [...prev, targetMonster]);
        addLog(`${targetMonster.name}は破壊された！`, 'damage');
      } else {
        // 生き残った場合、ATK_DOWN状態異常を付与
        setOpponentField(prev => {
          const newField = [...prev];
          const prevMonster = prev[targetIndex];
          if (!prevMonster) return newField;
          const updatedMonster = { ...prevMonster, currentHp: newHp };
          // 「次のターン終了時まで」= 2エンドフェイズ後
          statusEffectEngine.applyStatus(updatedMonster, STATUS_EFFECT_TYPES.ATK_DOWN, {
            value: 500,
            expiresAfterEndPhases: 2,
            source: 'C0000390',
            sourceName: '黒呪・ヴェルザクトの黒刃',
          });
          newField[targetIndex] = updatedMonster;
          return newField;
        });
        addLog(`${targetMonster.name}の攻撃力が500下がった！（次のターン終了時まで）`, 'info');
      }
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      applyEffect(validTargets[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '黒刃の対象モンスターを選択',
        targetPlayer: 'opponent',
        validIndices: validTargets.map(t => t.index),
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック：最初の対象に自動選択
    applyEffect(validTargets[0].index);
    return true;
  },

  /**
   * C0000393: 黒呪・カルヴェリオンの灰嵐
   * 相手フィールド全体に1500ダメージを与え、次のターン終了時まで相手モンスターの攻撃力を500下げる。
   * 場に《呪縛の塔・ヴェルナクール》がある場合、このカードのダメージを2000に変更。
   */
  C0000393: (skillText, context) => {
    const { addLog } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard, myFieldCard } = getPlayerContext(context);

    // ステータス効果エンジンをインポート
    const { statusEffectEngine, STATUS_EFFECT_TYPES } = require('../statusEffects');

    // 《呪縛の塔・ヴェルナクール》があるかチェック
    const hasVernacool = myFieldCard && myFieldCard.id === 'C0000386';
    const damage = hasVernacool ? 2000 : 1500;

    if (hasVernacool) {
      addLog('《呪縛の塔・ヴェルナクール》の効果でダメージが2000に変更！', 'info');
    }

    const destroyedMonsters: MonsterInstance[] = [];

    // 相手フィールド全体にダメージ + 生き残ったモンスターにATK_DOWNステータスを付与
    const newOpponentField = opponentField.map((monster) => {
      if (!monster) return null;

      const newHp = monster.currentHp - damage;
      addLog(`${monster.name}に${damage}ダメージ！`, 'damage');

      if (newHp <= 0) {
        destroyedMonsters.push(monster);
        addLog(`${monster.name}は破壊された！`, 'damage');
        return null;
      } else {
        // 生き残ったモンスターに攻撃力ダウンのステータス効果を付与
        // 「次のターン終了時まで」= 2エンドフェイズ後
        const updatedMonster = { ...monster, currentHp: newHp };
        statusEffectEngine.applyStatus(updatedMonster, STATUS_EFFECT_TYPES.ATK_DOWN, {
          value: 500,
          expiresAfterEndPhases: 2, // 次のターン終了時まで
          source: 'C0000393',
          sourceName: '黒呪・カルヴェリオンの灰嵐',
        });
        addLog(`${monster.name}の攻撃力が500下がった！（次のターン終了時まで）`, 'info');
        return updatedMonster;
      }
    });

    // フィールド更新
    setOpponentField(newOpponentField);

    // 破壊されたモンスターを墓地に送る
    if (destroyedMonsters.length > 0) {
      setOpponentGraveyard((prev) => [...prev, ...destroyedMonsters]);
    }

    return true;
  },

  /**
   * C0000075: シャドウ・バインド
   * 【刹那詠唱】相手のモンスター1体を動けなくする（1ターン行動不能）。
   */
  C0000075: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // 相手モンスターがいるか確認
    const validTargets = opponentField
      .map((m, idx) => m ? idx : -1)
      .filter(idx => idx !== -1);

    if (validTargets.length === 0) {
      addLog('相手の場にモンスターがいません', 'info');
      return false;
    }

    // 1体しかいない場合は自動選択
    if (validTargets.length === 1) {
      return selectAndApplyStatusToOpponent(
        context,
        STATUS_EFFECT_TYPES.STUN,
        { duration: 1 },
        'シャドウ・バインド'
      );
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '行動不能にするモンスターを選択',
        targetPlayer: 'opponent',
        validIndices: validTargets,
        callback: (selectedIndex: number) => {
          const { statusEffectEngine } = require('../statusEffects');
          setOpponentField(prev => prev.map((m, idx) => {
            if (idx === selectedIndex && m) {
              statusEffectEngine.applyStatus(m, STATUS_EFFECT_TYPES.STUN, {
                duration: 1,
                source: 'C0000075',
                sourceName: 'シャドウ・バインド',
              });
              addLog(`シャドウ・バインド: ${m.name}を行動不能にした！`, 'damage');
              return { ...m };
            }
            return m;
          }));
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック：自動選択
    return selectAndApplyStatusToOpponent(
      context,
      STATUS_EFFECT_TYPES.STUN,
      { duration: 1 },
      'シャドウ・バインド'
    );
  },

  /**
   * C0000079: 深淵の騎士ガルム
   * 基本技：このターンこのカードは2回攻撃ができる。
   */
  C0000079: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      if (monsterIndex === undefined || !myField[monsterIndex]) {
        addLog('対象のモンスターが見つかりません', 'info');
        return false;
      }

      // 2回攻撃フラグを設定
      setMyField(prev => {
        const newField = [...prev];
        const monster = newField[monsterIndex];
        if (!monster) return newField;
        newField[monsterIndex] = {
          ...monster,
          canDoubleAttack: true,
          attacksRemaining: 2,
        };
        return newField;
      });

      addLog('深淵の騎士ガルム: このターン2回攻撃が可能になった！', 'info');
      return true;
    }
    return false;
  },

  // ========================================
  // ID80-89 魔法カード効果
  // ========================================

  /**
   * C0000083: 闇の契約
   * 自分のライフを500減らす。デッキからコスト4以下闇属性モンスター1体を手札に加える。
   */
  C0000083: (skillText, context) => {
    const { addLog } = context;
    const { setMyLife, myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

    // ライフコスト支払い
    setMyLife(prev => prev - 500);
    addLog('闇の契約: ライフを500支払った', 'damage');

    // デッキからコスト4以下の闇属性モンスターを検索
    const targetCards = myDeck.filter(card =>
      card.type === 'monster' &&
      card.attribute === '闇' &&
      card.cost <= 4
    );

    if (targetCards.length === 0) {
      addLog('デッキにコスト4以下の闇属性モンスターがありません', 'info');
      return true; // ライフは支払ったので成功扱い
    }

    // ランダムに1体選択（または最初の1体）
    const selectedCard = targetCards[0];

    // デッキから除去して手札に加える
    setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
    setMyHand(prev => [...prev, selectedCard]);
    addLog(`闇の契約: ${selectedCard.name}を手札に加えた`, 'info');

    return true;
  },

  /**
   * C0000084: 腐食のコロード
   * 場にいるモンスター1体の攻撃力を800ダウン、0になると破壊。
   * このターン、自分の闇属性モンスターの攻撃力を300アップ。
   */
  C0000084: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 対象選択用のリスト作成（自分・相手両方のモンスター）
    const allTargets: { monster: MonsterInstance; index: number; isOpponent: boolean }[] = [];
    myField.forEach((m, idx) => {
      if (m) allTargets.push({ monster: m, index: idx, isOpponent: false });
    });
    opponentField.forEach((m, idx) => {
      if (m) allTargets.push({ monster: m, index: idx, isOpponent: true });
    });

    if (allTargets.length === 0) {
      addLog('場にモンスターがいません', 'info');
      return false;
    }

    // 攻撃力ダウン処理を行う関数
    const applyAttackDown = (targetIndex: number, isOpponent: boolean) => {
      const field = isOpponent ? opponentField : myField;
      const setField = isOpponent ? setOpponentField : setMyField;
      const monster = field[targetIndex];

      if (!monster) return;

      const newAtk = Math.max(0, (monster.currentAttack ?? monster.attack ?? 0) - 800);
      addLog(`腐食のコロード: ${monster.name}の攻撃力を800ダウン！（残り${newAtk}）`, 'damage');

      if (newAtk <= 0) {
        // 破壊
        setField(prev => {
          const newField = [...prev];
          newField[targetIndex] = null;
          return newField;
        });
        if (isOpponent) {
          setOpponentGraveyard(prev => [...prev, monster]);
        }
        addLog(`${monster.name}は破壊された！`, 'damage');
      } else {
        setField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            return { ...m, currentAttack: newAtk };
          }
          return m;
        }));
      }

      // 自分の闇属性モンスターの攻撃力を300アップ
      setMyField(prev => prev.map(m => {
        if (m && m.attribute === '闇') {
          const boostedAtk = (m.currentAttack ?? m.attack ?? 0) + 300;
          addLog(`${m.name}の攻撃力が300アップ！`, 'info');
          return { ...m, currentAttack: boostedAtk };
        }
        return m;
      }));
    };

    // 相手モンスターを優先選択（1体だけの場合は自動選択）
    const opponentTargets = allTargets.filter(t => t.isOpponent);
    if (opponentTargets.length === 1) {
      applyAttackDown(opponentTargets[0].index, true);
      return true;
    }

    // 複数ターゲットがある場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '攻撃力を800ダウンするモンスターを選択',
        targetPlayer: 'both',
        callback: (selectedIndex: number, isOpponent: boolean) => {
          applyAttackDown(selectedIndex, isOpponent);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック: 相手の最初のモンスターを選択
    if (opponentTargets.length > 0) {
      applyAttackDown(opponentTargets[0].index, true);
    } else if (allTargets.length > 0) {
      applyAttackDown(allTargets[0].index, allTargets[0].isOpponent);
    }
    return true;
  },

  /**
   * C0000086: 闇の波動
   * 相手プレイヤーに場にいる闇属性モンスターの数×400ダメージを与える。
   * 自分のライフを300減らす。
   */
  C0000086: (skillText, context) => {
    const { addLog } = context;
    const { myField, opponentField, setOpponentLife, setMyLife } = getPlayerContext(context);

    // 場にいる闘属性モンスターの数をカウント（自分・相手両方）
    const countDarkMonsters = (field: (MonsterInstance | null)[]) => field.filter(m => m && m.attribute === '闇').length;
    const darkCount = countDarkMonsters(myField) + countDarkMonsters(opponentField);

    const damage = darkCount * 400;
    addLog(`闇の波動: 場の闇属性モンスター${darkCount}体 × 400 = ${damage}ダメージ！`, 'damage');

    // 相手にダメージ
    if (damage > 0) {
      setOpponentLife(prev => prev - damage);
    }

    // 自分のライフを300減らす
    setMyLife(prev => prev - 300);
    addLog('闇の波動: 代償としてライフを300支払った', 'damage');

    return true;
  },

  /**
   * C0000087: シャドウ・チェインズ
   * 【刹那詠唱】相手モンスター1体の攻撃力をターン終了時まで0にし、
   * 相手モンスター1体に400ダメージを与える。
   */
  C0000087: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 相手モンスターがいるか確認
    const validTargets = opponentField
      .map((m, idx) => m ? idx : -1)
      .filter(idx => idx !== -1);

    if (validTargets.length === 0) {
      addLog('相手の場にモンスターがいません', 'info');
      return false;
    }

    // 効果適用関数
    const applyEffect = (targetIndex: number) => {
      const targetMonster = opponentField[targetIndex];
      if (!targetMonster) return;

      // 攻撃力を0に
      addLog(`シャドウ・チェインズ: ${targetMonster.name}の攻撃力を0にした！（ターン終了時まで）`, 'damage');

      // 400ダメージ
      const newHp = targetMonster.currentHp - 400;
      addLog(`シャドウ・チェインズ: ${targetMonster.name}に400ダメージ！`, 'damage');

      if (newHp <= 0) {
        // 破壊
        setOpponentField(prev => {
          const newField = [...prev];
          newField[targetIndex] = null;
          return newField;
        });
        setOpponentGraveyard(prev => [...prev, targetMonster]);
        addLog(`${targetMonster.name}は破壊された！`, 'damage');
      } else {
        setOpponentField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            return {
              ...m,
              currentAttack: 0,
              currentHp: newHp,
              atkZeroUntilEndOfTurn: true, // ターン終了時に解除するフラグ
            };
          }
          return m;
        }));
      }
    };

    // 1体だけの場合は自動選択
    if (validTargets.length === 1) {
      applyEffect(validTargets[0]);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '攻撃力を0にし400ダメージを与えるモンスターを選択',
        targetPlayer: 'opponent',
        validIndices: validTargets,
        callback: applyEffect,
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    applyEffect(validTargets[0]);
    return true;
  },

  /**
   * C0000088: ネクロフィアの儀式
   * 自分の場にいるモンスター1体をリリースし、
   * デッキから《ダーク・ネクロフィア》を場に直接召喚（コスト不要）。
   */
  C0000088: (skillText, context) => {
    const { addLog, setPendingMonsterTarget, summoningSickness = true } = context;
    const { myField, setMyField, myDeck, setMyDeck, setMyGraveyard, currentPlayer } = getPlayerContext(context);

    // 自分のフィールドにモンスターがいるか確認
    const myMonsters = myField
      .map((m, idx) => m ? idx : -1)
      .filter(idx => idx !== -1);

    if (myMonsters.length === 0) {
      addLog('リリースできるモンスターがいません', 'info');
      return false;
    }

    // デッキに《ダーク・ネクロフィア》があるか確認
    const necrophia = myDeck.find(card => card.name === 'ダーク・ネクロフィア');
    if (!necrophia) {
      addLog('デッキに《ダーク・ネクロフィア》がありません', 'info');
      return false;
    }

    // リリース処理とネクロフィア召喚を行う関数
    const performRitual = (releaseIndex: number) => {
      const releasedMonster = myField[releaseIndex];
      if (!releasedMonster) return;

      addLog(`ネクロフィアの儀式: ${releasedMonster.name}をリリース！`, 'info');

      // リリースしたモンスターを墓地に送る
      setMyGraveyard(prev => [...prev, releasedMonster]);

      // デッキからネクロフィアを除去
      setMyDeck(prev => prev.filter(c => c.uniqueId !== necrophia.uniqueId));

      // ネクロフィアを同じスロットに召喚
      setMyField(prev => {
        const newField = [...prev];
        newField[releaseIndex] = {
          ...necrophia,
          currentAttack: necrophia.attack ?? 0,
          currentHp: necrophia.hp ?? 0,
          canAttack: !summoningSickness, // 召喚酔い設定に依存
          owner: currentPlayer,
          charges: [],
          statusEffects: [],
        };
        return newField;
      });

      addLog(`ネクロフィアの儀式: 《ダーク・ネクロフィア》を特殊召喚！`, 'info');
    };

    // 1体だけの場合は自動選択
    if (myMonsters.length === 1) {
      performRitual(myMonsters[0]);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: 'リリースするモンスターを選択',
        targetPlayer: 'self',
        validIndices: myMonsters,
        callback: performRitual,
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    performRitual(myMonsters[0]);
    return true;
  },

  // ========================================
  // ID90-99 追加カード
  // ========================================

  /**
   * C0000092: シャドウ・レイヴン
   * 基本技：相手の手札を1枚破棄させる。
   */
  C0000092: (skillText, context) => {
    const { addLog } = context;
    const { opponentHand, setOpponentHand, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      if (opponentHand.length === 0) {
        addLog('相手の手札がありません', 'info');
        return true; // 効果は成功（対象なし）
      }

      // ランダムに1枚選択して墓地に送る
      const randomIndex = Math.floor(Math.random() * opponentHand.length);
      const discardedCard = opponentHand[randomIndex];

      setOpponentHand(prev => prev.filter((_, idx) => idx !== randomIndex));
      setOpponentGraveyard(prev => [...prev, discardedCard]);

      addLog(`シャドウ・レイヴンの基本技！相手の手札「${discardedCard.name}」を墓地に送った`, 'damage');
      return true;
    }

    return false;
  },

  /**
   * C0000093: ダーク・ヴォイド
   * 場にいるモンスター1体に1600ダメージを与える。
   */
  C0000093: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, setMyGraveyard, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 対象選択用のリスト作成（自分・相手両方のモンスター）
    const allTargets: { monster: MonsterInstance; index: number; isOpponent: boolean }[] = [];
    myField.forEach((m, idx) => {
      if (m) allTargets.push({ monster: m, index: idx, isOpponent: false });
    });
    opponentField.forEach((m, idx) => {
      if (m) allTargets.push({ monster: m, index: idx, isOpponent: true });
    });

    if (allTargets.length === 0) {
      addLog('場にモンスターがいません', 'info');
      return false;
    }

    // ダメージ処理を行う関数
    const applyDamage = (targetIndex: number, isOpponent: boolean) => {
      const field = isOpponent ? opponentField : myField;
      const setField = isOpponent ? setOpponentField : setMyField;
      const setGraveyard = isOpponent ? setOpponentGraveyard : setMyGraveyard;
      const monster = field[targetIndex];

      if (!monster) return;

      const newHp = monster.currentHp - 1600;
      addLog(`ダーク・ヴォイド: ${monster.name}に1600ダメージ！`, 'damage');

      if (newHp <= 0) {
        // 破壊
        setField(prev => {
          const newField = [...prev];
          newField[targetIndex] = null;
          return newField;
        });
        setGraveyard(prev => [...prev, monster]);
        addLog(`${monster.name}は破壊された！`, 'damage');
      } else {
        setField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            return { ...m, currentHp: newHp };
          }
          return m;
        }));
      }
    };

    // 相手モンスターを優先選択（1体だけの場合は自動選択）
    const opponentTargets = allTargets.filter(t => t.isOpponent);
    if (opponentTargets.length === 1 && opponentTargets.length === allTargets.length) {
      applyDamage(opponentTargets[0].index, true);
      return true;
    }

    // 複数ターゲットがある場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '1600ダメージを与えるモンスターを選択',
        targetPlayer: 'both',
        callback: (selectedIndex: number, isOpponent: boolean) => {
          applyDamage(selectedIndex, isOpponent);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック: 相手の最初のモンスターを選択
    if (opponentTargets.length > 0) {
      applyDamage(opponentTargets[0].index, true);
    } else if (allTargets.length > 0) {
      applyDamage(allTargets[0].index, allTargets[0].isOpponent);
    }
    return true;
  },

  /**
   * C0000097: 闇の女盗賊
   * 基本技：相手は1枚ドローする、魔法カードなら墓地に送り、自分のライフをそのコスト*200回復。
   * モンスターカードなら、相手のライフにそのコスト＊200ダメージを与える。
   */
  C0000097: (skillText, context) => {
    const { addLog } = context;
    const { setMyLife, setOpponentLife, opponentDeck, setOpponentDeck, opponentHand, setOpponentHand, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      if (opponentDeck.length === 0) {
        addLog('相手のデッキがありません', 'info');
        return false;
      }

      // 相手にドローさせる
      const drawnCard = opponentDeck[0];
      setOpponentDeck(prev => prev.slice(1));
      addLog(`相手は${drawnCard.name}をドローした`, 'info');

      if (drawnCard.type === 'magic') {
        // 魔法カードの場合：墓地に送り、コスト*200回復
        setOpponentGraveyard(prev => [...prev, drawnCard]);
        const healAmount = (drawnCard.cost || 0) * 200;
        setMyLife(prev => prev + healAmount);
        addLog(`闇の女盗賊の基本技！魔法カード「${drawnCard.name}」を墓地に送り、ライフを${healAmount}回復！`, 'heal');
      } else if (drawnCard.type === 'monster') {
        // モンスターカードの場合：手札に加え、コスト*200ダメージ
        setOpponentHand(prev => [...prev, drawnCard]);
        const damage = (drawnCard.cost || 0) * 200;
        setOpponentLife(prev => prev - damage);
        addLog(`闇の女盗賊の基本技！モンスターカード「${drawnCard.name}」で相手に${damage}ダメージ！`, 'damage');
      } else {
        // その他のカード（フィールドカードなど）
        setOpponentHand(prev => [...prev, drawnCard]);
        addLog(`闇の女盗賊の基本技！${drawnCard.name}は魔法でもモンスターでもない`, 'info');
      }

      return true;
    }

    return false;
  },

  /**
   * C0000100: 闇の収集
   * 相手の墓地のカードを2枚除外し、デッキから闇属性モンスター1枚を手札に加える。
   */
  C0000100: (skillText, context) => {
    const { addLog } = context;
    const { opponentGraveyard, setOpponentGraveyard } = getPlayerContext(context);

    // 相手の墓地から2枚除外
    if (opponentGraveyard.length < 2) {
      addLog('相手の墓地にカードが2枚以上ありません', 'info');
      return false;
    }

    // 2枚を除外（ランダムに選択）
    const graveyardCopy = [...opponentGraveyard];
    const exiledCards = [];
    for (let i = 0; i < 2 && graveyardCopy.length > 0; i++) {
      const randomIndex = Math.floor(Math.random() * graveyardCopy.length);
      exiledCards.push(graveyardCopy.splice(randomIndex, 1)[0]);
    }

    setOpponentGraveyard(graveyardCopy);
    addLog(`相手の墓地から${exiledCards.map(c => c.name).join('、')}を除外`, 'info');

    // デッキから闇属性モンスターをサーチ
    const foundCard = searchCard(context, (card) => {
      return card.attribute === '闇' && card.type === 'monster';
    });

    if (foundCard) {
      addLog(`${foundCard.name}を手札に加えた`, 'info');
    } else {
      addLog('デッキに闇属性モンスターがありませんでした', 'info');
    }

    return true;
  },

  /**
   * C0000101: シャドウ・カース
   * 相手モンスター1体の攻撃力をターン終了時まで0にし、
   * そのモンスターの効果を次のターン終了時まで無効化。
   */
  C0000101: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    const monsters = opponentField.filter((m) => m !== null);
    if (monsters.length === 0) {
      addLog('相手フィールドにモンスターがいません', 'info');
      return false;
    }

    // 攻撃力0 + SILENCE状態異常を付与するヘルパー関数
    const applyEffect = (targetIndex: number) => {
      // 攻撃力を0にする
      setOpponentField((prev) => {
        const newField = [...prev];
        if (newField[targetIndex]) {
          const target = newField[targetIndex];
          addLog(`${target.name}の攻撃力が0になった！`, 'info');
          newField[targetIndex] = {
            ...target,
            currentAttack: 0,
          };
        }
        return newField;
      });
      // SILENCE状態異常を付与（次のターン終了時まで = duration: 2）
      applyStatusToOpponentMonster(context, targetIndex, STATUS_EFFECT_TYPES.SILENCE, {
        duration: 2,
      }, 'シャドウ・カース');
    };

    // 対象選択が必要
    if (monsters.length > 1 && setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: 'シャドウ・カースの対象を選択',
        validTargets: opponentField.map((m, i) => (m ? i : -1)).filter((i) => i >= 0),
        isOpponentField: true,
        callback: applyEffect,
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // 1体のみの場合は自動選択
    const targetIndex = opponentField.findIndex((m) => m !== null);
    applyEffect(targetIndex);
    return true;
  },

  /**
   * C0000102: 暗黒の略奪
   * 手札を1枚捨てる。そのコスト以下の相手の場にいる禁忌カード以外のモンスター1体のコントロールを奪う。
   * エンドフェイズに、そのモンスターを破壊して自分に800ダメージ。
   */
  C0000102: (skillText, context) => {
    const { addLog, setPendingHandSelection, setPendingMonsterTarget, summoningSickness = true } = context;
    const { myHand, setMyHand, myField, setMyField, opponentField, setOpponentField, currentPlayer } =
      getPlayerContext(context);

    if (myHand.length === 0) {
      addLog('手札がありません', 'info');
      return false;
    }

    // 手札選択が必要
    if (setPendingHandSelection) {
      setPendingHandSelection({
        message: '捨てるカードを選択（そのコスト以下のモンスターを奪う）',
        callback: (selectedCards: CardWithId[]) => {
          if (!selectedCards || selectedCards.length === 0) return;
          const selectedCard = selectedCards[0];
          // 手札から削除
          setMyHand((prev) => prev.filter((c) => c.uniqueId !== selectedCard.uniqueId));
          addLog(`${selectedCard.name}を捨てた（コスト: ${selectedCard.cost || 0}）`, 'info');

          const discardedCost = selectedCard.cost || 0;

          // 対象となるモンスターを検索
          const validTargets = opponentField
            .map((m, i) => ({ monster: m, index: i }))
            .filter(
              ({ monster }) =>
                monster &&
                !monster.forbidden &&
                (monster.cost || 0) <= discardedCost
            );

          if (validTargets.length === 0) {
            addLog('コントロールを奪える対象がいません', 'info');
            return;
          }

          // 自分の場の空きスロットを確認
          const emptySlotIndex = myField.findIndex((slot) => slot === null);
          if (emptySlotIndex === -1) {
            addLog('自分の場が満杯です', 'info');
            return;
          }

          // 対象選択
          if (validTargets.length > 1 && setPendingMonsterTarget) {
            setPendingMonsterTarget({
              message: 'コントロールを奪うモンスターを選択',
              validTargets: validTargets.map((t) => t.index),
              isOpponentField: true,
              callback: (targetIndex: number) => {
                const stolenMonster = opponentField[targetIndex];
                if (!stolenMonster) return;
                // 相手の場から削除
                setOpponentField((prev) => {
                  const newField = [...prev];
                  newField[targetIndex] = null;
                  return newField;
                });
                // 自分の場に追加（エンドフェイズに破壊フラグ）
                setMyField((prev) => {
                  const newField = [...prev];
                  newField[emptySlotIndex] = {
                    ...stolenMonster,
                    owner: currentPlayer,
                    canAttack: !summoningSickness, // 召喚酔い設定に依存
                    destroyAtEndOfTurn: true,
                    damageOwnerOnDestroy: 800,
                  };
                  return newField;
                });
                addLog(`${stolenMonster.name}のコントロールを奪った！`, 'info');
              },
              selectionOwner: context.currentPlayer,
            });
          } else {
            // 1体のみ
            const { monster: stolenMonster, index: targetIndex } = validTargets[0];
            if (!stolenMonster) return;
            setOpponentField((prev) => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });
            setMyField((prev) => {
              const newField = [...prev];
              newField[emptySlotIndex] = {
                ...stolenMonster,
                owner: currentPlayer,
                canAttack: !summoningSickness, // 召喚酔い設定に依存
                destroyAtEndOfTurn: true,
                damageOwnerOnDestroy: 800,
              };
              return newField;
            });
            addLog(`${stolenMonster.name}のコントロールを奪った！`, 'info');
          }
        },
      });
      return true;
    }

    return false;
  },

  /**
   * C0000103: 奈落の呼び声
   * 自分の墓地のコスト4以下闇属性モンスター1体を場に戻し、そのターン終了時まで攻撃力を600アップ。
   */
  C0000103: (skillText, context) => {
    const { addLog, setPendingGraveyardSelection, summoningSickness = true } = context;
    const { myGraveyard, setMyGraveyard, myField, setMyField, currentPlayer } = getPlayerContext(context);

    // 対象となるモンスターを検索
    const validMonsters = myGraveyard.filter(
      (card) =>
        card.type === 'monster' &&
        card.attribute === '闇' &&
        (card.cost || 0) <= 4
    );

    if (validMonsters.length === 0) {
      addLog('墓地にコスト4以下の闇属性モンスターがいません', 'info');
      return false;
    }

    // 空きスロット確認
    const emptySlotIndex = myField.findIndex((slot) => slot === null);
    if (emptySlotIndex === -1) {
      addLog('場が満杯です', 'info');
      return false;
    }

    // 墓地選択が必要
    if (validMonsters.length > 1 && setPendingGraveyardSelection) {
      setPendingGraveyardSelection({
        message: '蘇生するコスト4以下の闇属性モンスターを選択',
        cards: validMonsters,
        callback: (selectedCards: CardWithId[] | CardWithId | null) => {
          const targetCard = Array.isArray(selectedCards) ? selectedCards[0] : selectedCards;
          if (!targetCard) return;
          // 墓地から削除
          setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== targetCard.uniqueId));
          // 場に蘇生（攻撃力+600）
          setMyField((prev) => {
            const newField = [...prev];
            const boostedAttack = (targetCard.attack || 0) + 600;
            newField[emptySlotIndex] = {
              ...targetCard,
              currentAttack: boostedAttack,
              currentHp: targetCard.hp ?? 0,
              maxHp: targetCard.hp,
              canAttack: !summoningSickness, // 召喚酔い設定に依存
              owner: currentPlayer,
              charges: [],
              statusEffects: [],
              usedSkillThisTurn: false,
              atkBoostUntilEndOfTurn: 600,
            };
            return newField;
          });
          addLog(`${targetCard.name}を蘇生！攻撃力が600アップ！`, 'info');
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // 1体のみの場合
    const targetCard = validMonsters[0];
    setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== targetCard.uniqueId));
    setMyField((prev) => {
      const newField = [...prev];
      const boostedAttack = (targetCard.attack || 0) + 600;
      newField[emptySlotIndex] = {
        ...targetCard,
        currentAttack: boostedAttack,
        currentHp: targetCard.hp ?? 0,
        maxHp: targetCard.hp,
        canAttack: !summoningSickness, // 召喚酔い設定に依存
        owner: currentPlayer,
        charges: [],
        statusEffects: [],
        usedSkillThisTurn: false,
        atkBoostUntilEndOfTurn: 600,
      };
      return newField;
    });
    addLog(`${targetCard.name}を蘇生！攻撃力が600アップ！`, 'info');

    return true;
  },

  /**
   * C0000104: 闇の崩壊
   * 場にいる全てのモンスターの攻撃力を半分にし、相手モンスター全体にその差分の合計ダメージを与える。
   */
  C0000104: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField, opponentField, setOpponentField, setOpponentGraveyard } =
      getPlayerContext(context);

    let totalDamage = 0;

    // 自分のモンスターの攻撃力を半減し、差分を計算
    setMyField((prev) => {
      return prev.map((monster) => {
        if (monster) {
          const oldAttack = monster.currentAttack || monster.attack || 0;
          const newAttack = Math.floor(oldAttack / 2);
          const diff = oldAttack - newAttack;
          totalDamage += diff;
          addLog(`${monster.name}の攻撃力: ${oldAttack} → ${newAttack}`, 'info');
          return { ...monster, currentAttack: newAttack };
        }
        return monster;
      });
    });

    // 相手のモンスターの攻撃力を半減し、差分を計算
    const opponentDiffs = [];
    setOpponentField((prev) => {
      return prev.map((monster) => {
        if (monster) {
          const oldAttack = monster.currentAttack || monster.attack || 0;
          const newAttack = Math.floor(oldAttack / 2);
          const diff = oldAttack - newAttack;
          totalDamage += diff;
          opponentDiffs.push({ name: monster.name, oldAttack, newAttack });
          addLog(`${monster.name}の攻撃力: ${oldAttack} → ${newAttack}`, 'info');
          return { ...monster, currentAttack: newAttack };
        }
        return monster;
      });
    });

    addLog(`攻撃力差分の合計: ${totalDamage}`, 'info');

    // 相手モンスター全体に差分ダメージ
    if (totalDamage > 0) {
      const destroyedMonsters: MonsterInstance[] = [];
      setOpponentField((prev) => {
        return prev.map((monster) => {
          if (monster) {
            const newHp = monster.currentHp - totalDamage;
            addLog(`${monster.name}に${totalDamage}ダメージ！（残りHP: ${Math.max(0, newHp)}）`, 'damage');
            if (newHp <= 0) {
              destroyedMonsters.push(monster);
              return null;
            }
            return { ...monster, currentHp: newHp };
          }
          return monster;
        });
      });

      // 破壊されたモンスターを墓地に送る
      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard((prev) => [...prev, ...destroyedMonsters]);
        destroyedMonsters.forEach((m) => addLog(`${m.name}は破壊された！`, 'damage'));
      }
    }

    return true;
  },

  // ========================================
  // ID110-119 追加カード
  // ========================================

  /**
   * C0000112: シャドウ・エンフォーサー
   * 基本技：相手の墓地のカードを2枚除外し、その中にモンスターがあれば相手プレイヤーに600ダメージを与える。
   */
  C0000112: (skillText, context) => {
    const { addLog } = context;
    const { opponentGraveyard, setOpponentGraveyard, setOpponentLife } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      if (opponentGraveyard.length < 2) {
        addLog('相手の墓地にカードが2枚以上ありません', 'info');
        return false;
      }

      // 2枚を除外（ランダムに選択）
      const graveyardCopy = [...opponentGraveyard];
      const exiledCards = [];
      for (let i = 0; i < 2 && graveyardCopy.length > 0; i++) {
        const randomIndex = Math.floor(Math.random() * graveyardCopy.length);
        exiledCards.push(graveyardCopy.splice(randomIndex, 1)[0]);
      }

      setOpponentGraveyard(graveyardCopy);
      addLog(`シャドウ・エンフォーサーの基本技！相手の墓地から${exiledCards.map(c => c.name).join('、')}を除外`, 'info');

      // モンスターが含まれていれば600ダメージ
      const hasMonster = exiledCards.some(c => c.type === 'monster');
      if (hasMonster) {
        setOpponentLife(prev => prev - 600);
        addLog('除外したカードにモンスターが含まれていた！相手に600ダメージ！', 'damage');
      }

      return true;
    }

    return false;
  },

  /**
   * C0000117: 禁忌の波動
   * 場にいる全てのモンスターの効果をターン終了時まで無効化し、
   * 相手モンスター全体に場の闇属性モンスター1体につき400ダメージを与える。
   */
  C0000117: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 場にいる全てのモンスターにSILENCE状態異常を付与（ターン終了時まで）
    // 自分のモンスター
    setMyField(prev => prev.map(m => {
      if (m) {
        statusEffectEngine.applyStatus(m, STATUS_EFFECT_TYPES.SILENCE, {
          duration: 0, // このターン終了時まで
          source: 'C0000117',
          sourceName: '禁忌の波動',
        });
        return { ...m };
      }
      return m;
    }));

    // 相手のモンスター
    applyStatusToAllOpponentMonsters(context, STATUS_EFFECT_TYPES.SILENCE, {
      duration: 0, // このターン終了時まで
    }, '禁忌の波動');

    addLog('禁忌の波動: 場の全モンスターの効果を無効化！', 'info');

    // 場の闇属性モンスターをカウント（自分・相手両方）
    const countDarkMonsters = (field: (MonsterInstance | null)[]) => field.filter(m => m && m.attribute === '闇').length;
    const darkCount = countDarkMonsters(myField) + countDarkMonsters(opponentField);

    const damage = darkCount * 400;

    if (damage > 0) {
      addLog(`場の闇属性モンスター${darkCount}体 × 400 = ${damage}ダメージ！`, 'damage');

      // 相手モンスター全体にダメージ
      const destroyedMonsters: MonsterInstance[] = [];
      setOpponentField(prev => prev.map(monster => {
        if (monster) {
          const newHp = monster.currentHp - damage;
          addLog(`${monster.name}に${damage}ダメージ！`, 'damage');

          if (newHp <= 0) {
            destroyedMonsters.push(monster);
            return null;
          }
          return { ...monster, currentHp: newHp };
        }
        return monster;
      }));

      // 破壊されたモンスターを墓地に送る
      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
        destroyedMonsters.forEach(m => addLog(`${m.name}は破壊された！`, 'damage'));
      }
    } else {
      addLog('場に闇属性モンスターがいないためダメージなし', 'info');
    }

    return true;
  },

  /**
   * C0000118: シャドウ・ドミニオン
   * 手札からカード1枚と追加で闇属性カードを任意の枚数捨てる。
   * 追加で捨てたカード1枚につき1体、禁忌カード以外の相手の場にいるモンスターのコントロールをターン終了時まで奪う。
   * その後、奪ったモンスター1体につき、自分のライフを1000減らす。
   */
  C0000118: (skillText, context) => {
    const { addLog, setPendingHandSelection } = context;
    const { myHand, setMyHand, myField, setMyField, opponentField, setOpponentField, setMyLife, setMyGraveyard, currentPlayer } = getPlayerContext(context);

    if (myHand.length === 0) {
      addLog('手札がありません', 'info');
      return false;
    }

    // 簡易実装: 手札の闇属性カードを全て捨てて、その数だけモンスターを奪う
    // 複雑なUI選択が必要なため、自動選択で実装

    // 手札から闇属性カードを検索
    const darkCards = myHand.filter(c => c.attribute === '闇');
    const nonDarkCards = myHand.filter(c => c.attribute !== '闇');

    if (nonDarkCards.length === 0 && darkCards.length === 0) {
      addLog('手札がありません', 'info');
      return false;
    }

    // 最低1枚は捨てる必要がある
    let discardCount = 0;
    const discardedCards: CardWithId[] = [];

    // 非闇属性カードから1枚捨てる（なければ闇属性）
    if (nonDarkCards.length > 0) {
      discardedCards.push(nonDarkCards[0]);
      discardCount = 0; // 追加で捨てたカードは0
    } else if (darkCards.length > 0) {
      discardedCards.push(darkCards[0]);
      discardCount = 0;
    }

    // 追加で闇属性カードを捨てる（コントロール奪取対象数を決定）
    const remainingDarkCards = darkCards.filter(c => !discardedCards.includes(c));

    // 相手フィールドの奪える対象
    const stealableMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && !monster.forbidden);

    // 奪えるモンスターの数と捨てられる闇属性カードの数の少ない方
    const maxSteal = Math.min(stealableMonsters.length, remainingDarkCards.length);

    // 空きスロットの確認
    const emptySlots = myField.map((m, idx) => m === null ? idx : -1).filter(idx => idx !== -1);
    const actualSteal = Math.min(maxSteal, emptySlots.length);

    // 追加で闇属性カードを捨てる
    for (let i = 0; i < actualSteal; i++) {
      discardedCards.push(remainingDarkCards[i]);
      discardCount++;
    }

    // 手札からカードを捨てる
    setMyHand(prev => prev.filter(c => !discardedCards.some(d => d.uniqueId === c.uniqueId)));
    setMyGraveyard(prev => [...prev, ...discardedCards]);
    addLog(`シャドウ・ドミニオン: ${discardedCards.map(c => c.name).join('、')}を捨てた`, 'info');

    if (actualSteal === 0) {
      addLog('コントロールを奪えるモンスターがいないか、空きスロットがありません', 'info');
      return true;
    }

    // コントロールを奪う
    let totalLifeLoss = 0;
    for (let i = 0; i < actualSteal; i++) {
      const { monster, index } = stealableMonsters[i];
      if (!monster) continue;
      const emptySlot = emptySlots[i];

      // 相手の場から削除
      setOpponentField(prev => {
        const newField = [...prev];
        newField[index] = null;
        return newField;
      });

      // 自分の場に追加（ターン終了時に返す）
      setMyField(prev => {
        const newField = [...prev];
        newField[emptySlot] = {
          ...monster,
          owner: currentPlayer,
          canAttack: true,
          returnToOpponentAtEndOfTurn: true,
        };
        return newField;
      });

      addLog(`${monster.name}のコントロールを奪った！（ターン終了時まで）`, 'info');
      totalLifeLoss += 1000;
    }

    // ライフ減少
    if (totalLifeLoss > 0) {
      setMyLife(prev => prev - totalLifeLoss);
      addLog(`コントロール奪取の代償: ライフを${totalLifeLoss}支払った`, 'damage');
    }

    return true;
  },

  /**
   * C0000119: 闇の宣告
   * 相手の手札を全て公開し、その中から1枚を選んで墓地に送る。
   * このターン、自分の闇属性モンスターの攻撃力を400アップ。
   */
  C0000119: (skillText, context) => {
    const { addLog, setPendingHandSelection } = context;
    const { myField, setMyField, opponentHand, setOpponentHand, setOpponentGraveyard } = getPlayerContext(context);

    // 相手の手札を公開
    if (opponentHand.length === 0) {
      addLog('相手の手札がありません', 'info');
    } else {
      addLog(`闇の宣告: 相手の手札を公開！ ${opponentHand.map(c => c.name).join('、')}`, 'info');

      // ランダムに1枚選んで墓地に送る（簡易実装）
      // 本来は選択UIが必要だが、自動選択で実装
      const randomIndex = Math.floor(Math.random() * opponentHand.length);
      const discardedCard = opponentHand[randomIndex];

      setOpponentHand(prev => prev.filter((_, idx) => idx !== randomIndex));
      setOpponentGraveyard(prev => [...prev, discardedCard]);
      addLog(`${discardedCard.name}を墓地に送った！`, 'damage');
    }

    // 自分の闇属性モンスターの攻撃力を400アップ
    let buffedCount = 0;
    setMyField(prev => prev.map(monster => {
      if (monster && monster.attribute === '闇') {
        buffedCount++;
        const newAttack = (monster.currentAttack ?? monster.attack ?? 0) + 400;
        return { ...monster, currentAttack: newAttack };
      }
      return monster;
    }));

    if (buffedCount > 0) {
      addLog(`自分の闇属性モンスター${buffedCount}体の攻撃力が400アップ！`, 'info');
    }

    return true;
  },

  /**
   * C0000111: 禁忌の守護者
   * 基本技：自分のモンスター１体に守護（1度だけ受けるダメージを半減）を付与する
   */
  C0000111: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 自分フィールドのモンスターを検索
      const validTargets = myField
        .map((m, i) => ({ monster: m, index: i }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('禁忌の守護者: 場にモンスターがいません', 'info');
        return false;
      }

      setPendingMonsterTarget({
        message: '守護を付与するモンスターを選択',
        validTargets: validTargets.map(t => t.index),
        targetPlayer: 'self',
        callback: (targetIndex: number) => {
          setMyField(prev => {
            const newField = [...prev];
            const target = newField[targetIndex];
            if (!target) return newField;

            // 守護状態を付与
            const newStatusEffects = [...(target.statusEffects || [])];
            newStatusEffects.push({
              type: STATUS_EFFECT_TYPES.GUARD,
              duration: -1, // 使用されるまで永続
              source: 'C0000111',
              sourceName: '禁忌の守護者',
            });

            newField[targetIndex] = {
              ...target,
              statusEffects: newStatusEffects,
            };
            return newField;
          });

          addLog(`禁忌の守護者の基本技: ${myField[targetIndex]?.name || 'モンスター'}に守護を付与！`, 'info');
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    return false;
  },

  /**
   * C0000108: 虚蝕の呪詛 (Phase Card)
   * 3枚重ね: 相手フィールドのモンスター1体を攻撃力を0にし（次のターン終了時まで）、
   * 自分の闇属性モンスター全体の攻撃力をターン終了時まで800アップ。
   * その後、このカードを墓地に送り、次のターン開始時、自分のライフを1000減らす。
   */
  C0000108: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField, setMyLife } = getPlayerContext(context);

    if (skillText.includes('3枚重ね') || skillText.includes('３枚重ね')) {
      // 相手モンスターを選択してATK=0
      const validTargets = opponentField
        .map((m, i) => ({ monster: m, index: i }))
        .filter(({ monster }) => monster !== null);

      const applyDarkBuff = () => {
        // 自分の闇属性モンスター全体のATK+800
        let buffCount = 0;
        setMyField(prev => {
          return prev.map(monster => {
            if (monster && monster.attribute === '闇') {
              buffCount++;
              const newAttack = (monster.currentAttack ?? monster.attack ?? 0) + 800;
              return {
                ...monster,
                currentAttack: newAttack,
                attackBuffUntilEndOfTurn: (monster.attackBuffUntilEndOfTurn || 0) + 800,
              };
            }
            return monster;
          });
        });
        if (buffCount > 0) {
          addLog(`虚蝕の呪詛【3枚重ね】: 闇属性モンスター${buffCount}体の攻撃力+800！`, 'info');
        }

        // 次のターン開始時に1000ダメージ（フラグを設定）
        // Note: 実際のダメージ処理はターン開始時に別途必要
        addLog('虚蝕の呪詛【3枚重ね】: 次のターン開始時に自分に1000ダメージ！', 'damage');
        setMyLife(prev => prev - 1000);
      };

      if (validTargets.length > 0) {
        setPendingMonsterTarget({
          message: '攻撃力を0にするモンスターを選択',
          validTargets: validTargets.map(t => t.index),
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            setOpponentField(prev => {
              const newField = [...prev];
              const target = newField[targetIndex];
              if (!target) return newField;
              newField[targetIndex] = {
                ...target,
                currentAttack: 0,
                attackModifiedUntilNextTurnEnd: true,
              };
              addLog(`虚蝕の呪詛【3枚重ね】: ${target.name}の攻撃力を0に！`, 'damage');
              return newField;
            });
            applyDarkBuff();
          },
          selectionOwner: context.currentPlayer,
        });
      } else {
        addLog('虚蝕の呪詛【3枚重ね】: 対象となる相手モンスターがいません', 'info');
        applyDarkBuff();
      }

      return true;
    }

    return false;
  },

  /**
   * C0000387: 呪核の杖・ザルヴェリオ (Phase Card)
   * 3枚重ね: このカードを墓地に送る。その後、自分の場に《呪骸王エリザルヴェドン》がいる場合、
   * その攻撃力をターン終了時まで1500アップし、このターン、《黒呪》魔法カードが与えるダメージを倍にする。
   * 場にいない場合、デッキから《呪骸王エリザルヴェドン》を場に召喚する（コスト不要）。
   */
  C0000387: (skillText, context) => {
    const { addLog, currentPlayer, summoningSickness = true } = context;
    const { myField, setMyField, myDeck, setMyDeck } = getPlayerContext(context);

    if (skillText.includes('3枚重ね') || skillText.includes('３枚重ね')) {
      // 《呪骸王エリザルヴェドン》が場にいるか確認
      const elizalvedonIndex = myField.findIndex(m =>
        m && m.name && m.name.includes('呪骸王エリザルヴェドン')
      );

      if (elizalvedonIndex !== -1) {
        // いる場合: ATK+1500
        setMyField(prev => {
          const newField = [...prev];
          const monster = newField[elizalvedonIndex];
          if (monster) {
            newField[elizalvedonIndex] = {
              ...monster,
              currentAttack: (monster.currentAttack ?? monster.attack ?? 0) + 1500,
              attackBuffUntilEndOfTurn: (monster.attackBuffUntilEndOfTurn || 0) + 1500,
            };
          }
          return newField;
        });
        addLog('呪核の杖・ザルヴェリオ【3枚重ね】: 《呪骸王エリザルヴェドン》の攻撃力+1500！', 'info');
        addLog('※《黒呪》魔法カードダメージ倍加効果は未実装', 'info');
      } else {
        // いない場合: デッキから召喚
        const emptySlot = myField.findIndex(slot => slot === null);
        if (emptySlot === -1) {
          addLog('呪核の杖・ザルヴェリオ【3枚重ね】: 場が満杯で召喚できません', 'info');
          return true;
        }

        const elizalvedon = myDeck.find(card =>
          card.type === 'monster' && !!card.name && card.name.includes('呪骸王エリザルヴェドン')
        );

        if (elizalvedon) {
          // デッキから除去
          setMyDeck(prev => prev.filter(c => c.uniqueId !== elizalvedon.uniqueId));

          // 場に召喚
          setMyField(prev => {
            const newField = [...prev];
            const slot = newField.findIndex(s => s === null);
            if (slot !== -1) {
              newField[slot] = {
                ...elizalvedon,
                currentHp: elizalvedon.hp ?? 0,
                currentAttack: elizalvedon.attack ?? 0,
                maxHp: elizalvedon.hp,
                canAttack: !summoningSickness, // 召喚酔い設定に依存
                charges: [],
                statusEffects: [],
                usedSkillThisTurn: false,
                owner: currentPlayer,
              };
            }
            return newField;
          });
          addLog(`呪核の杖・ザルヴェリオ【3枚重ね】: デッキから《呪骸王エリザルヴェドン》を召喚！`, 'info');
        } else {
          addLog('呪核の杖・ザルヴェリオ【3枚重ね】: デッキに《呪骸王エリザルヴェドン》がいません', 'info');
        }
      }

      return true;
    }

    return false;
  },

  /**
   * C0000395: 血呪の棺・ヴェルディクタス (Phase Card)
   * 3枚重ね: このカードを墓地に送る。その後、デッキから《魔女エリザヴェット・ヴェイル》を
   * 自分の場に特殊召喚する（コスト不要）。場に既にいる場合、《魔女エリザヴェット・ヴェイル》の
   * 攻撃力をターン終了時まで1000アップし、自分のデッキから1枚ドローする。
   */
  C0000395: (skillText, context) => {
    const { addLog, registerCardTriggers, continuousEffectEngine, summoningSickness = true } = context;
    const { myField, setMyField, myDeck, setMyDeck, setMyHand, currentPlayer } = getPlayerContext(context);

    if (skillText.includes('3枚重ね') || skillText.includes('３枚重ね')) {
      // 《魔女エリザヴェット・ヴェイル》が場にいるか確認
      const elizavetteVeilIndex = myField.findIndex(m =>
        m && m.name && m.name === '魔女エリザヴェット・ヴェイル'
      );

      if (elizavetteVeilIndex !== -1) {
        // いる場合: ATK+1000 + 1枚ドロー
        setMyField(prev => {
          const newField = [...prev];
          const monster = newField[elizavetteVeilIndex];
          if (monster) {
            newField[elizavetteVeilIndex] = {
              ...monster,
              currentAttack: (monster.currentAttack ?? monster.attack ?? 0) + 1000,
              attackBuffUntilEndOfTurn: (monster.attackBuffUntilEndOfTurn || 0) + 1000,
            };
          }
          return newField;
        });
        addLog('血呪の棺・ヴェルディクタス【3枚重ね】: 《魔女エリザヴェット・ヴェイル》の攻撃力+1000！', 'info');

        // 1枚ドロー
        if (myDeck.length > 0) {
          const drawnCard = myDeck[0];
          setMyDeck(prev => prev.slice(1));
          setMyHand(prev => [...prev, drawnCard]);
          addLog(`血呪の棺・ヴェルディクタス【3枚重ね】: ${drawnCard.name}をドロー！`, 'info');
        } else {
          addLog('血呪の棺・ヴェルディクタス【3枚重ね】: デッキにカードがありません', 'info');
        }
      } else {
        // いない場合: デッキから召喚
        const emptySlot = myField.findIndex(slot => slot === null);
        if (emptySlot === -1) {
          addLog('血呪の棺・ヴェルディクタス【3枚重ね】: 場が満杯で召喚できません', 'info');
          return true;
        }

        const elizavetteVeil = myDeck.find(card =>
          card.type === 'monster' && card.name === '魔女エリザヴェット・ヴェイル'
        );

        if (elizavetteVeil) {
          // デッキから除去
          setMyDeck(prev => prev.filter(c => c.uniqueId !== elizavetteVeil.uniqueId));

          // 場に召喚
          const summonedMonster: MonsterInstance = {
            ...elizavetteVeil,
            currentHp: elizavetteVeil.hp ?? 0,
            currentAttack: elizavetteVeil.attack ?? 0,
            maxHp: elizavetteVeil.hp,
            canAttack: !summoningSickness, // 召喚酔い設定に依存
            charges: [],
            statusEffects: [],
            usedSkillThisTurn: false,
            owner: currentPlayer,
            uniqueId: `${elizavetteVeil.id}_${Date.now()}_${Math.random()}`,
          };

          setMyField(prev => {
            const newField = [...prev];
            const slot = newField.findIndex(s => s === null);
            if (slot !== -1) {
              newField[slot] = summonedMonster;
            }
            return newField;
          });

          // トリガー登録
          if (registerCardTriggers) {
            registerCardTriggers(summonedMonster, currentPlayer, emptySlot);
          }

          // 常時効果登録
          if (continuousEffectEngine) {
            continuousEffectEngine.register(summonedMonster, currentPlayer);
          }

          addLog(`血呪の棺・ヴェルディクタス【3枚重ね】: デッキから《魔女エリザヴェット・ヴェイル》を特殊召喚！`, 'info');
        } else {
          addLog('血呪の棺・ヴェルディクタス【3枚重ね】: デッキに《魔女エリザヴェット・ヴェイル》がいません', 'info');
        }
      }

      return true;
    }

    return false;
  },

  /**
   * C0000120: 禁忌の儀式
   * 自分の場にいるモンスター2体をリリースし、デッキから《ブラック・オラクル》を場に直接召喚（コスト不要）。
   */
  C0000120: (skillText, context) => {
    const { addLog, setPendingMonsterTarget, registerCardTriggers, continuousEffectEngine, summoningSickness = true } = context;
    const { myField, setMyField, myDeck, setMyDeck, myGraveyard, setMyGraveyard, currentPlayer } = getPlayerContext(context);

    // 自分の場のモンスターを取得
    const myMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (myMonsters.length < 2) {
      addLog('禁忌の儀式: 場にモンスターが2体以上必要です', 'info');
      return false;
    }

    // デッキに《ブラック・オラクル》がいるか確認
    const blackOracle = myDeck.find(c => c.name === 'ブラック・オラクル');
    if (!blackOracle) {
      addLog('禁忌の儀式: デッキに《ブラック・オラクル》がいません', 'info');
      return false;
    }

    // 2体選択する処理
    let selectedMonsters: number[] = [];

    const selectFirstMonster = () => {
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: 'リリースする1体目のモンスターを選択',
          targetPlayer: 'self',
          callback: (firstIndex: number) => {
            selectedMonsters.push(firstIndex);
            selectSecondMonster(firstIndex);
          },
          selectionOwner: context.currentPlayer,
        });
      } else {
        // フォールバック: 最初の2体を自動選択
        selectedMonsters = [myMonsters[0].index, myMonsters[1].index];
        performRitual();
      }
    };

    const selectSecondMonster = (firstIndex: number) => {
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: 'リリースする2体目のモンスターを選択',
          targetPlayer: 'self',
          validTargets: myMonsters.filter(m => m.index !== firstIndex).map(m => m.index),
          callback: (secondIndex: number) => {
            selectedMonsters.push(secondIndex);
            performRitual();
          },
          selectionOwner: context.currentPlayer,
        });
      }
    };

    const performRitual = () => {
      // リリースするモンスターを墓地に送る
      const releasedMonsters = selectedMonsters.map(idx => myField[idx]).filter((m): m is MonsterInstance => m !== null);

      // フィールドからモンスターを除去
      setMyField(prev => {
        const newField = [...prev];
        selectedMonsters.forEach(idx => {
          newField[idx] = null;
        });
        return newField;
      });

      // 墓地に送る
      setMyGraveyard(prev => [...prev, ...releasedMonsters.map(m => m as unknown as CardWithId)]);

      addLog(`禁忌の儀式: ${releasedMonsters.map(m => m.name).join('と')}をリリース！`, 'info');

      // デッキから《ブラック・オラクル》を取り出して召喚
      setMyDeck(prev => prev.filter(c => c.uniqueId !== blackOracle.uniqueId));

      // 空きスロットを探す
      const emptySlot = myField.findIndex((s, idx) => s === null || selectedMonsters.includes(idx));

      const summonedMonster: MonsterInstance = {
        ...blackOracle,
        currentHp: blackOracle.hp ?? 0,
        maxHp: blackOracle.hp,
        currentAttack: blackOracle.attack ?? 0,
        canAttack: !summoningSickness, // 召喚酔い設定に依存
        charges: [],
        statusEffects: [],
        usedSkillThisTurn: false,
        owner: currentPlayer,
        uniqueId: `${blackOracle.id}_ritual_${Date.now()}`,
      };

      setMyField(prev => {
        const newField = [...prev];
        // 最初の空きスロットに召喚
        const slot = newField.findIndex(s => s === null);
        if (slot !== -1) {
          newField[slot] = summonedMonster;
        }
        return newField;
      });

      // トリガー登録
      if (registerCardTriggers) {
        registerCardTriggers(summonedMonster, currentPlayer, emptySlot);
      }

      // 常時効果登録
      if (continuousEffectEngine) {
        continuousEffectEngine.register(summonedMonster, currentPlayer);
      }

      addLog(`禁忌の儀式: デッキから《ブラック・オラクル》を召喚！`, 'info');
    };

    selectFirstMonster();
    return true;
  },

  /**
   * C0000201: 地獄開城の契約【禁忌カード】
   * 自分のライフを2000減らし、自分のデッキからモンスターを3体まで場に召喚（コスト不要）する。
   * このターン、それらのモンスターの攻撃力は倍になる。
   * ターン終了時、それらのモンスターを全て破壊し、その攻撃力合計分のダメージを自分は受ける。
   */
  C0000201: (skillText, context) => {
    const { addLog, setPendingDeckReview, registerCardTriggers, continuousEffectEngine } = context;
    const {
      myField, setMyField, myDeck, setMyDeck, myGraveyard, setMyGraveyard,
      setMyLife, currentPlayer,
    } = getPlayerContext(context);

    // 空きスロット確認
    const emptySlots = myField.filter(slot => slot === null).length;
    if (emptySlots === 0) {
      addLog('地獄開城の契約: 場に空きスロットがありません', 'info');
      return false;
    }

    // デッキにモンスターがいるか確認
    const deckMonsters = myDeck.filter(c => c.type === 'monster');
    if (deckMonsters.length === 0) {
      addLog('地獄開城の契約: デッキにモンスターがいません', 'info');
      return false;
    }

    // ライフを2000減らす
    setMyLife(prev => Math.max(0, prev - 2000));
    addLog('地獄開城の契約: ライフを2000支払った！', 'damage');

    // 召喚可能な数（最大3体、空きスロット数、デッキのモンスター数のうち最小）
    const summonCount = Math.min(3, emptySlots, deckMonsters.length);

    // デッキ選択UI（複数選択）
    setPendingDeckReview({
      message: `召喚するモンスターを${summonCount}体まで選択してください`,
      cards: deckMonsters,
      count: summonCount,
      callback: (selectedCards: CardWithId[]) => {
        if (selectedCards.length === 0) {
          addLog('地獄開城の契約: モンスターを選択しませんでした', 'info');
          return;
        }

        const summonedIds = [];
        const selectedUniqueIds = selectedCards.map(c => c.uniqueId);

        // デッキから選択したカードを除去
        setMyDeck(prev => prev.filter(c => !selectedUniqueIds.includes(c.uniqueId)));

        // 選択したモンスターを召喚（攻撃力2倍）
        setMyField(prev => {
          const newField = [...prev];
          selectedCards.forEach(card => {
            const slot = newField.findIndex(s => s === null);
            if (slot !== -1) {
              const summonedMonster: MonsterInstance = {
                ...card,
                currentHp: card.hp ?? 0,
                maxHp: card.hp,
                // 攻撃力2倍
                currentAttack: (card.attack ?? 0) * 2,
                originalAttackBeforeContract: card.attack, // 元の攻撃力を保存
                canAttack: true, // このカードで召喚したモンスターは即座に攻撃可能
                charges: [],
                statusEffects: [],
                usedSkillThisTurn: false,
                owner: currentPlayer,
                uniqueId: `${card.id}_contract_${Date.now()}_${slot}`,
                // ターン終了時の破壊フラグ
                hellContractMonster: true,
              };
              newField[slot] = summonedMonster;
              summonedIds.push(summonedMonster.uniqueId);

              // トリガー登録
              if (registerCardTriggers) {
                registerCardTriggers(summonedMonster, currentPlayer, slot);
              }

              // 常時効果登録
              if (continuousEffectEngine) {
                continuousEffectEngine.register(summonedMonster, currentPlayer);
              }
            }
          });
          return newField;
        });

        addLog(`地獄開城の契約: ${selectedCards.map(c => c.name).join('、')}を召喚！攻撃力2倍！`, 'info');
        addLog('地獄開城の契約: ターン終了時にこれらのモンスターは破壊され、攻撃力合計分のダメージを受けます', 'damage');

        // ターン終了時の処理はmagic-spirit.jsxのエンドフェイズ処理で
        // hellContractMonsterフラグを持つモンスターを処理する必要がある
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000315: シャドウ・ネクロレイヴン
   * 基本技: チャージを消費する。相手の手札を1枚ランダムに墓地に捨てる。
   */
  C0000315: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, setMyField, opponentHand, setOpponentHand, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('モンスターが存在しません', 'info');
        return false;
      }

      // チャージがあるか確認
      if (!monster.charges || monster.charges.length === 0) {
        addLog('チャージがありません', 'info');
        return false;
      }

      // 相手の手札があるか確認
      if (opponentHand.length === 0) {
        addLog('相手の手札がありません', 'info');
        return false;
      }

      // チャージを1つ消費
      setMyField(prev => {
        const newField = [...prev];
        const monster = newField[monsterIndex];
        if (monster) {
          const newCharges = [...(monster.charges || [])];
          newCharges.pop(); // 最後のチャージを消費
          newField[monsterIndex] = { ...monster, charges: newCharges };
        }
        return newField;
      });

      // 相手の手札からランダムに1枚選んで墓地に送る
      const randomIndex = Math.floor(Math.random() * opponentHand.length);
      const discardedCard = opponentHand[randomIndex];

      setOpponentHand(prev => prev.filter((_, idx) => idx !== randomIndex));
      setOpponentGraveyard(prev => [...prev, discardedCard]);

      addLog(`シャドウ・ネクロレイヴンの基本技: チャージを消費して「${discardedCard.name}」を捨てさせた！`, 'damage');
      return true;
    }
    return false;
   },
   /**
   * C0000237: 幼魔王女の誘惑（魔法カード）【刹那詠唱】
   * 場に《リリカ》と名のつくモンスターがいるとき発動でき、
   * 禁忌カード以外の相手モンスター1体のコントロールをターン終了時まで奪う。
   */
  C0000237: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField, currentPlayer } = getPlayerContext(context);

    // 場に《リリカ》がいるか確認
    const hasLilika = myField.some(m =>
      m && m.name && m.name.includes('リリカ')
    );

    if (!hasLilika) {
      addLog('幼魔王女の誘惑: 場に《リリカ》モンスターがいません', 'info');
      return false;
    }

    // 空きスロットを確認
    const emptySlotIndex = myField.findIndex(slot => slot === null);
    if (emptySlotIndex === -1) {
      addLog('幼魔王女の誘惑: 自分の場に空きがありません', 'info');
      return false;
    }

    // 相手の場に禁忌カード以外のモンスターがいるか
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && !monster.forbidden);

    if (validTargets.length === 0) {
      addLog('幼魔王女の誘惑: 奪えるモンスターがいません', 'info');
      return false;
    }

    // モンスター選択
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: 'コントロールを奪うモンスターを選択',
        targetPlayer: 'opponent',
        validTargets: validTargets.map(t => t.index),
        callback: (selectedIndex: number) => {
          const targetMonster = opponentField[selectedIndex];
          if (targetMonster) {
            // 相手の場から除去
            setOpponentField(prev => {
              const newField = [...prev];
              newField[selectedIndex] = null;
              return newField;
            });

            // 自分の場に移動（ターン終了時に戻る）
            const stolenMonster: MonsterInstance = {
              ...targetMonster,
              controlStolen: true,
              originalOwner: (currentPlayer === 1 ? 2 : 1) as PlayerNumber,
              originalSlot: selectedIndex,
              controlReturnTurn: 0, // このターン終了時に戻る
              canAttack: true, // このカードの場合は攻撃可能
            };

            setMyField(prev => {
              const newField = [...prev];
              const slot = newField.findIndex(s => s === null);
              if (slot !== -1) {
                newField[slot] = stolenMonster;
              }
              return newField;
            });

            addLog(`幼魔王女の誘惑: ${targetMonster.name}のコントロールを奪った！`, 'info');
          }
        },
        selectionOwner: context.currentPlayer,
      });
    }

    return true;
  },

  /**
   * C0000364: ヴォランティス・エクリプス
   * 基本技: 相手モンスター1体を次のターン終了時まで行動不能にする
   * 上級技: 自分のSPトークンを3個レスト状態にし、相手の手札をランダムに2枚墓地に送る
   */
  C0000364: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, opponentHand, setOpponentHand, setOpponentGraveyard, myActiveSP, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

    // 基本技: 相手モンスター1体を次のターン終了時まで行動不能にする
    if (context.skillType === 'basic') {
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('エクリプスの基本技: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      const applyStun = (targetIndex: number) => {
        const targetMonster = opponentField[targetIndex];
        if (!targetMonster) return;

        setOpponentField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            return {
              ...m,
              stunUntilNextTurnEnd: true, // 次のターン終了時まで行動不能
              canAttack: false,
            };
          }
          return m;
        }));
        addLog(`エクリプスの基本技: ${targetMonster.name}を行動不能にした！（次のターン終了時まで）`, 'damage');
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyStun(validTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '行動不能にする相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyStun(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyStun(validTargets[0].index);
      return true;
    }

    // 上級技: SP3個レスト、相手の手札をランダムに2枚墓地に送る
    if (context.skillType === 'advanced') {
      if (myActiveSP < 3) {
        addLog('エクリプスの上級技: アクティブSPが3個以上必要です', 'info');
        return false;
      }

      if (opponentHand.length === 0) {
        addLog('エクリプスの上級技: 相手の手札がありません', 'info');
        return false;
      }

      // SP3個をレスト状態に
      setMyActiveSP(prev => prev - 3);
      setMyRestedSP(prev => prev + 3);
      addLog('エクリプスの上級技: SPを3個消費！', 'info');

      // 手札からランダムに2枚選択
      const discardCount = Math.min(2, opponentHand.length);
      const shuffledHand = [...opponentHand].sort(() => Math.random() - 0.5);
      const discardedCards = shuffledHand.slice(0, discardCount);
      const discardedUniqueIds = discardedCards.map(c => c.uniqueId);

      // 手札から削除
      setOpponentHand(prev => prev.filter(c => !discardedUniqueIds.includes(c.uniqueId)));

      // 墓地に送る
      setOpponentGraveyard(prev => [...prev, ...discardedCards]);

      discardedCards.forEach(card => {
        addLog(`エクリプスの上級技: 相手の手札から「${card.name}」を墓地に送った！`, 'damage');
      });

      return true;
    }

    return false;
  },

  /**
   * C0000388: 黒呪・ファルザノンの影爪（魔法カード）【刹那詠唱】
   * 自分のデッキの上から1枚を墓地に送り相手モンスター1体に1000ダメージを与える。
   * 【刹那詠唱】: SPトークン1個を追加でレスト状態にすることで相手ターンでも発動可能。
   */
  C0000388: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const {
      myDeck, setMyDeck, setMyGraveyard,
      opponentField, setOpponentField, setOpponentGraveyard,
    } = getPlayerContext(context);

    // デッキが空なら失敗
    if (myDeck.length === 0) {
      addLog('黒呪・ファルザノンの影爪: デッキが空です', 'info');
      return false;
    }

    // 相手モンスターがいるか確認
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('黒呪・ファルザノンの影爪: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    // デッキの上から1枚を墓地に送る
    const milledCard = myDeck[0];
    setMyDeck(prev => prev.slice(1));
    setMyGraveyard(prev => [...prev, milledCard]);
    addLog(`黒呪・ファルザノンの影爪: ${milledCard.name}を墓地に送った`, 'info');

    // 相手モンスターに1000ダメージ
    const applyDamage = (targetIndex: number) => {
      setOpponentField(prev => {
        const newField = [...prev];
        const target = newField[targetIndex];
        if (target) {
          const newHp = target.currentHp - 1000;
          if (newHp <= 0) {
            // 破壊
            setOpponentGraveyard(grave => [...grave, target]);
            addLog(`黒呪・ファルザノンの影爪: ${target.name}に1000ダメージ！破壊！`, 'damage');
            newField[targetIndex] = null;
          } else {
            newField[targetIndex] = {
              ...target,
              currentHp: newHp,
            };
            addLog(`黒呪・ファルザノンの影爪: ${target.name}に1000ダメージ！（残りHP: ${newHp}）`, 'damage');
          }
        }
        return newField;
      });
    };

    if (validTargets.length === 1) {
      applyDamage(validTargets[0].index);
    } else {
      setPendingMonsterTarget({
        message: '1000ダメージを与える相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyDamage(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
    }

    return true;
  },

  /**
   * C0000389: 黒呪・ザルガノートの呪鎖（魔法カード）【刹那詠唱】
   * 相手モンスター1体を次のターン終了時まで行動不能にし、
   * 毎ターン終了時、そのモンスターに400ダメージを与える（最大2ターン）。
   */
  C0000389: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // 相手モンスターがいるか確認
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('黒呪・ザルガノートの呪鎖: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    // 効果適用
    const applyChain = (targetIndex: number) => {
      setOpponentField(prev => {
        const newField = [...prev];
        const target = newField[targetIndex];
        if (target) {
          // 行動不能 + 毎ターン400ダメージ（2ターン）
          const newStatusEffects = [...(target.statusEffects || [])];
          newStatusEffects.push({
            type: STATUS_EFFECT_TYPES.STUN,
            duration: 2, // 次のターン終了時まで（2ターン持続）
            source: 'C0000389',
            sourceName: '黒呪・ザルガノートの呪鎖',
          });

          newField[targetIndex] = {
            ...target,
            statusEffects: newStatusEffects,
            canAttack: false,
            // 呪鎖ダメージフラグ
            cursedChain: {
              damage: 400,
              turnsRemaining: 2,
              source: '黒呪・ザルガノートの呪鎖',
            },
          };
          addLog(`黒呪・ザルガノートの呪鎖: ${target.name}を呪鎖で拘束！（行動不能、毎ターン400ダメージ×2ターン）`, 'damage');
        }
        return newField;
      });
    };

    if (validTargets.length === 1) {
      applyChain(validTargets[0].index);
    } else {
      setPendingMonsterTarget({
        message: '呪鎖で拘束する相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyChain(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
    }

    return true;
  },

  /**
   * C0000407: レイスヴィット
   * 基本技: 相手の手札をランダムに1枚墓地に送り、相手モンスター1体に送ったカードのコスト×200のダメージを与える
   */
  C0000407: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentHand, setOpponentHand, setOpponentGraveyard, opponentField, setOpponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手の手札があるか確認
      if (opponentHand.length === 0) {
        addLog('レイスヴィットの基本技: 相手の手札がありません', 'info');
        return false;
      }

      // ランダムに1枚選択
      const randomIndex = Math.floor(Math.random() * opponentHand.length);
      const discardedCard = opponentHand[randomIndex];
      const damage = discardedCard.cost * 200;

      // 手札から墓地へ
      setOpponentHand(prev => prev.filter((_, idx) => idx !== randomIndex));
      setOpponentGraveyard(prev => [...prev, discardedCard]);
      addLog(`レイスヴィットの基本技: 「${discardedCard.name}」（コスト${discardedCard.cost}）を墓地に送った！`, 'damage');

      // 相手モンスターがいない場合はダメージなし
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('レイスヴィットの基本技: 相手フィールドにモンスターがいないためダメージなし', 'info');
        return true;
      }

      // ダメージ適用
      const applyDamage = (targetIndex: number) => {
        setOpponentField(prev => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target) {
            const newHp = target.currentHp - damage;
            if (newHp <= 0) {
              setOpponentGraveyard(g => [...g, target]);
              addLog(`レイスヴィットの基本技: ${target.name}に${damage}ダメージで破壊！`, 'damage');
              newField[targetIndex] = null;
            } else {
              newField[targetIndex] = { ...target, currentHp: newHp };
              addLog(`レイスヴィットの基本技: ${target.name}に${damage}ダメージ！（残HP: ${newHp}）`, 'damage');
            }
          }
          return newField;
        });
      };

      // 1体のみなら自動選択
      if (validTargets.length === 1) {
        applyDamage(validTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      setPendingMonsterTarget({
        message: `${damage}ダメージを与える相手モンスターを選択`,
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyDamage(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });

      return true;
    }

    return false;
  },

  /**
   * C0000409: バンシーディス
   * 基本技: 相手のデッキの上から2枚を墓地に送り、相手に400ダメージ
   */
  C0000409: (skillText, context) => {
    const { addLog } = context;
    const { opponentDeck, setOpponentDeck, setOpponentGraveyard, setOpponentLife } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手デッキから墓地に送る
      const millCount = Math.min(2, opponentDeck.length);
      if (millCount > 0) {
        const milledCards = opponentDeck.slice(0, millCount);
        setOpponentDeck(prev => prev.slice(millCount));
        setOpponentGraveyard(prev => [...prev, ...milledCards]);
        addLog(`バンシーディスの基本技: 相手のデッキから${millCount}枚を墓地に送った（${milledCards.map(c => c.name).join('、')}）`, 'damage');
      } else {
        addLog('バンシーディスの基本技: 相手のデッキがありません', 'info');
      }

      // 相手に400ダメージ
      setOpponentLife(prev => Math.max(0, prev - 400));
      addLog('バンシーディスの基本技: 相手に400ダメージ！', 'damage');

      return true;
    }

    return false;
  },

  /**
   * C0000391: 黒呪・トゥルヴァントの障壁
   * 自分のモンスター1体に守護を付与（ダメージ半減1回）
   * 《呪縛の塔・ヴェルナクール》がいれば全体付与
   */
  C0000391: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);
    const { statusEffectEngine, STATUS_EFFECT_TYPES } = require('../statusEffects');

    // 《呪縛の塔・ヴェルナクール》チェック
    const hasVernacur = myField.some(m => m && m.name === '呪縛の塔・ヴェルナクール');

    if (hasVernacur) {
      // 全体に守護付与
      setMyField(prev => {
        const newField = [...prev];
        newField.forEach((monster, i) => {
          if (monster) {
            statusEffectEngine.applyStatus(monster, STATUS_EFFECT_TYPES.GUARD, {
              duration: -1,
              source: 'C0000391',
              sourceName: '黒呪・トゥルヴァントの障壁',
            });
          }
        });
        return newField;
      });
      addLog('黒呪・トゥルヴァントの障壁: 《呪縛の塔・ヴェルナクール》の力で全モンスターに守護を付与！', 'info');
      return true;
    }

    // 通常: 1体選択
    const validTargets = myField
      .map((monster, index) => ({ monster, index }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('黒呪・トゥルヴァントの障壁: 対象モンスターがいません', 'info');
      return false;
    }

    if (validTargets.length === 1) {
      const target = validTargets[0].monster;
      if (!target) return false;
      statusEffectEngine.applyStatus(target, STATUS_EFFECT_TYPES.GUARD, {
        duration: -1,
        source: 'C0000391',
        sourceName: '黒呪・トゥルヴァントの障壁',
      });
      addLog(`黒呪・トゥルヴァントの障壁: ${target.name}に守護を付与！`, 'info');
      return true;
    }

    setPendingMonsterTarget({
      message: '守護を付与するモンスターを選択',
      targetPlayer: 'self',
      callback: (selectedIndex: number) => {
        setMyField(prev => {
          const newField = [...prev];
          const target = newField[selectedIndex];
          if (target) {
            statusEffectEngine.applyStatus(target, STATUS_EFFECT_TYPES.GUARD, {
              duration: -1,
              source: 'C0000391',
              sourceName: '黒呪・トゥルヴァントの障壁',
            });
            addLog(`黒呪・トゥルヴァントの障壁: ${target.name}に守護を付与！`, 'info');
          }
          return newField;
        });
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000392: 黒呪・ドルヴァネクの傀儡糸
   * 相手のコスト4以下のモンスター1体のコントロールを奪う
   * 奪ったモンスターが破壊されたら相手に800ダメージ
   */
  C0000392: (skillText, context) => {
    const { addLog, setPendingMonsterTarget, summoningSickness = true } = context;
    const { myField, setMyField, opponentField, setOpponentField, setOpponentLife } = getPlayerContext(context);

    // コスト4以下の相手モンスター取得
    const validTargets = opponentField
      .map((monster, index) => ({ monster, index }))
      .filter(({ monster }) => monster !== null && monster.cost <= 4);

    if (validTargets.length === 0) {
      addLog('黒呪・ドルヴァネクの傀儡糸: 対象となるコスト4以下のモンスターがいません', 'info');
      return false;
    }

    // 自分のフィールドに空きがあるかチェック
    const emptySlot = myField.findIndex(slot => slot === null);
    if (emptySlot === -1) {
      addLog('黒呪・ドルヴァネクの傀儡糸: フィールドに空きがありません', 'info');
      return false;
    }

    const stealMonster = (targetIndex: number) => {
      const target = opponentField[targetIndex];
      if (!target) return;

      // 相手フィールドから除去
      setOpponentField(prev => {
        const newField = [...prev];
        newField[targetIndex] = null;
        return newField;
      });

      // 自分フィールドに追加（傀儡糸の呪い付き）
      setMyField(prev => {
        const newField = [...prev];
        const stolenMonster = {
          ...target,
          puppetCurse: true,  // 破壊時800ダメージのマーカー
          canAttack: !summoningSickness, // 召喚酔い設定に依存
        };
        const slot = newField.findIndex(s => s === null);
        if (slot !== -1) {
          newField[slot] = stolenMonster;
        }
        return newField;
      });

      addLog(`黒呪・ドルヴァネクの傀儡糸: ${target.name}のコントロールを奪った！`, 'info');
    };

    if (validTargets.length === 1) {
      stealMonster(validTargets[0].index);
      return true;
    }

    setPendingMonsterTarget({
      message: 'コントロールを奪うコスト4以下のモンスターを選択',
      targetPlayer: 'opponent',
      validTargets: validTargets.map(t => t.index),
      callback: (selectedIndex: number) => {
        stealMonster(selectedIndex);
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000394: 黒呪・エルザフィールの契約
   * 相手の全モンスターに2500ダメージ
   * 《エリザヴェット・ヴェイル》がいれば効果無効化も付与
   */
  C0000394: (skillText, context) => {
    const { addLog } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);
    const { statusEffectEngine, STATUS_EFFECT_TYPES } = require('../statusEffects');

    // 《エリザヴェット・ヴェイル》チェック（魔女エリザヴェット・ヴェイル含む）
    const hasElizabeth = myField.some((m, slotIndex) => {
      if (m !== null && m !== undefined && !m.name) {
        console.warn('[dark.js C0000394] フィールドにnameがないモンスターがいます:', {
          slotIndex,
          monsterKeys: Object.keys(m),
          monster: m,
          sourceCard: context.card?.name,
        });
        return false;
      }
      return m && m.name && m.name.includes('エリザヴェット・ヴェイル');
    });

    const destroyedMonsters: MonsterInstance[] = [];
    setOpponentField(prev => {
      const newField = [...prev];
      newField.forEach((monster, i) => {
        if (monster) {
          // 効果無効化（エリザヴェットがいる場合）
          if (hasElizabeth) {
            statusEffectEngine.applyStatus(monster, STATUS_EFFECT_TYPES.SILENCE, {
              duration: 1,  // ターン終了まで
              source: 'C0000394',
              sourceName: '黒呪・エルザフィールの契約',
            });
          }

          // 2500ダメージ
          const newHp = monster.currentHp - 2500;
          if (newHp <= 0) {
            destroyedMonsters.push(monster);
            newField[i] = null;
          } else {
            newField[i] = { ...monster, currentHp: newHp };
          }
        }
      });
      return newField;
    });

    // 破壊されたモンスターを墓地に
    if (destroyedMonsters.length > 0) {
      setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
    }

    const hitCount = opponentField.filter(m => m !== null).length;
    if (hitCount > 0) {
      addLog(`黒呪・エルザフィールの契約: 相手全モンスターに2500ダメージ！（${destroyedMonsters.length}体破壊）`, 'damage');
      if (hasElizabeth) {
        addLog('黒呪・エルザフィールの契約: 《エリザヴェット・ヴェイル》の力で効果も無効化！', 'info');
      }
    } else {
      addLog('黒呪・エルザフィールの契約: 相手フィールドにモンスターがいません', 'info');
    }

    return true;
  },

  /**
   * C0000425: 灰骨竜フレアスケルス
   * 基本技: SPトークン1個をレスト状態にし、相手フィールド全体に1000ダメージ（ターンに1度）。
   */
  C0000425: (skillText, context) => {
    const { addLog } = context;
    const {
      myActiveSP,
      setMyActiveSP,
      myRestedSP,
      setMyRestedSP,
      opponentField,
      setOpponentField,
      setOpponentGraveyard,
    } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // SPが1以上あるか確認
      if (myActiveSP < 1) {
        addLog('SPが足りません', 'info');
        return false;
      }

      // SPを1レスト状態にする
      setMyActiveSP(myActiveSP - 1);
      setMyRestedSP(myRestedSP + 1);
      addLog('灰骨竜フレアスケルス: SPトークン1個をレスト状態に', 'info');

      // 相手フィールドにモンスターがいるか確認
      const targetMonsters = opponentField.filter(m => m !== null);
      if (targetMonsters.length === 0) {
        addLog('灰骨竜フレアスケルス: 相手フィールドにモンスターがいません', 'info');
        return true; // SPは消費済み
      }

      // 相手フィールド全体に1000ダメージ
      const destroyedMonsters: MonsterInstance[] = [];
      setOpponentField(prev => {
        return prev.map(monster => {
          if (monster) {
            const newHp = (monster.currentHp ?? monster.hp ?? 0) - 1000;
            addLog(`灰骨竜フレアスケルス: ${monster.name}に1000ダメージ！`, 'damage');
            if (newHp <= 0) {
              destroyedMonsters.push(monster);
              return null;
            }
            return { ...monster, currentHp: newHp };
          }
          return monster;
        });
      });

      // 破壊されたモンスターを相手の墓地へ
      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
        destroyedMonsters.forEach(m => {
          addLog(`${m.name}は破壊された！`, 'damage');
        });
      }

      return true;
    }

    return false;
  },

  /**
   * C0000110: 闇の支配者
   * 基本技: 相手の手札を1枚ランダムに捨てる。その後、相手は1枚ドローする。
   * 上級技: 相手のSPトークンを1つ減らす。
   */
  C0000110: (skillText, context) => {
    const { addLog } = context;
    const {
      opponentHand, setOpponentHand,
      opponentDeck, setOpponentDeck,
      setOpponentGraveyard,
      opponentActiveSP, setOpponentActiveSP,
    } = getPlayerContext(context);

    if (context.skillType === 'advanced') {
      // 上級技: 相手のSPトークンを1つ減らす
      if (opponentActiveSP <= 0) {
        addLog('闇の支配者: 相手のアクティブSPがありません', 'info');
        return false;
      }

      setOpponentActiveSP(opponentActiveSP - 1);
      addLog('闇の支配者: 相手のSPトークンを1つ減らした', 'damage');
      return true;
    }

    if (context.skillType === 'basic') {
      // 相手の手札が空か確認
      if (opponentHand.length === 0) {
        addLog('闇の支配者: 相手の手札がありません', 'info');
        return false;
      }

      // 相手の手札を1枚ランダムに捨てる
      const randomIndex = Math.floor(Math.random() * opponentHand.length);
      const discardedCard = opponentHand[randomIndex];
      setOpponentHand(prev => prev.filter((_, i) => i !== randomIndex));
      setOpponentGraveyard(prev => [...prev, discardedCard]);
      addLog(`闇の支配者: 相手の手札から${discardedCard.name}を捨てさせた`, 'damage');

      // 相手は1枚ドロー
      if (opponentDeck.length > 0) {
        const drawnCard = opponentDeck[0];
        setOpponentDeck(prev => prev.slice(1));
        setOpponentHand(prev => [...prev, drawnCard]);
        addLog('闇の支配者: 相手は1枚ドローした', 'info');
      } else {
        addLog('闇の支配者: 相手のデッキがありません', 'info');
      }

      return true;
    }

    return false;
  },

  /**
   * C0000564: 黒霧の呪術師
   * 基本技: 相手モンスター1体を【彷徨い】状態にする。このターン、このカードは攻撃できない。
   */
  C0000564: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { setMyField, opponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手フィールドにモンスターがいない場合
      const hasOpponentMonster = opponentField.some((m: any) => m !== null);
      if (!hasOpponentMonster) {
        addLog('黒霧の呪術師: 相手モンスターがいません', 'info');
        return false;
      }

      // このカードを攻撃不可にする
      if (monsterIndex !== undefined) {
        setMyField((prev: any) => {
          const newField = [...prev];
          if (newField[monsterIndex]) {
            newField[monsterIndex] = {
              ...newField[monsterIndex],
              canAttack: false,
            };
          }
          return newField;
        });
      }

      // 相手モンスター1体を選択して【彷徨い】状態にする
      selectAndApplyStatusToOpponent(
        context,
        STATUS_EFFECT_TYPES.WANDERING,
        {
          duration: 3,
        },
        '黒霧の呪術師'
      );

      addLog('黒霧の呪術師: このターン攻撃できなくなった', 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000565: 虚ろの収集家
   * 基本技: 相手モンスター1体を【彷徨い】状態にする。
   */
  C0000565: (skillText, context) => {
    const { addLog } = context;
    const { opponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手フィールドにモンスターがいない場合
      const hasOpponentMonster = opponentField.some((m: any) => m !== null);
      if (!hasOpponentMonster) {
        addLog('虚ろの収集家: 相手モンスターがいません', 'info');
        return false;
      }

      // 相手モンスター1体を選択して【彷徨い】状態にする
      selectAndApplyStatusToOpponent(
        context,
        STATUS_EFFECT_TYPES.WANDERING,
        {
          duration: 3,
        },
        '虚ろの収集家'
      );

      return true;
    }

    return false;
  },

  /**
   * C0000566: 彷徨いの王・ワンダラー
   * 基本技: 相手の【彷徨い】状態のモンスターの数×1枚ドローする。
   */
  C0000566: (skillText, context) => {
    const { addLog } = context;
    const { opponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手の【彷徨い】状態のモンスターをカウント
      const wanderingCount = opponentField.filter((m: any) => {
        if (!m?.statusEffects) return false;
        return (m.statusEffects as Array<{ type: string }>).some(
          s => s.type === STATUS_EFFECT_TYPES.WANDERING
        );
      }).length;

      if (wanderingCount === 0) {
        addLog('彷徨いの王・ワンダラー: 相手に【彷徨い】状態のモンスターがいません', 'info');
        return false;
      }

      // 彷徨い数分ドロー
      drawCards(context, wanderingCount);
      addLog(`彷徨いの王・ワンダラー: ${wanderingCount}枚ドロー！`, 'info');
      return true;
    }

    return false;
  },

  // ========================================
  // 彷徨い関連魔法カード
  // ========================================

  /**
   * C0000568: 魂散らし
   * コスト4以下の相手モンスター1体を【彷徨い】状態にする。
   */
  C0000568: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, currentPlayer } = getPlayerContext(context);

    // コスト4以下の相手モンスターを抽出
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter((item): item is { monster: MonsterInstance; index: number } => {
        if (!item.monster) return false;
        // コスト4以下のみ
        return (item.monster.cost || 0) <= 4;
      });

    if (validTargets.length === 0) {
      addLog('魂散らし: コスト4以下の相手モンスターがいません', 'info');
      return false;
    }

    // 状態異常を適用する関数
    const applyWandering = (targetIndex: number) => {
      setOpponentField(prev => prev.map((m, idx) => {
        if (idx === targetIndex && m) {
          const result = statusEffectEngine.applyStatus(m, STATUS_EFFECT_TYPES.WANDERING, {
            duration: 3,
            source: 'C0000568',
            sourceName: '魂散らし',
          });
          if (result.success) {
            addLog(`魂散らし: ${m.name}を【彷徨い】状態にした！`, 'damage');
          }
          return { ...m };
        }
        return m;
      }));
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      applyWandering(validTargets[0].index);
      return true;
    }

    // 複数の場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '【彷徨い】にするコスト4以下のモンスターを選択',
        targetPlayer: 'opponent',
        validTargets: validTargets.map(t => t.index),
        callback: (selectedIndex: number) => {
          applyWandering(selectedIndex);
        },
        selectionOwner: currentPlayer,
      });
    } else {
      applyWandering(validTargets[0].index);
    }

    return true;
  },

  /**
   * C0000569: 彷徨いの霧
   * 相手モンスター1体を【彷徨い】状態にする。さらに1枚ドロー。
   */
  C0000569: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, currentPlayer } = getPlayerContext(context);

    // 相手モンスターを抽出
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter((item): item is { monster: MonsterInstance; index: number } => item.monster !== null);

    if (validTargets.length === 0) {
      addLog('彷徨いの霧: 相手モンスターがいません', 'info');
      return false;
    }

    // 状態異常を適用してドローする関数
    const applyWanderingAndDraw = (targetIndex: number) => {
      setOpponentField(prev => prev.map((m, idx) => {
        if (idx === targetIndex && m) {
          const result = statusEffectEngine.applyStatus(m, STATUS_EFFECT_TYPES.WANDERING, {
            duration: 3,
            source: 'C0000569',
            sourceName: '彷徨いの霧',
          });
          if (result.success) {
            addLog(`彷徨いの霧: ${m.name}を【彷徨い】状態にした！`, 'damage');
          }
          return { ...m };
        }
        return m;
      }));

      // 1枚ドロー
      drawCards(context, 1);
      addLog('彷徨いの霧: さらに1枚ドロー！', 'info');
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      applyWanderingAndDraw(validTargets[0].index);
      return true;
    }

    // 複数の場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '【彷徨い】にするモンスターを選択',
        targetPlayer: 'opponent',
        validTargets: validTargets.map(t => t.index),
        callback: (selectedIndex: number) => {
          applyWanderingAndDraw(selectedIndex);
        },
        selectionOwner: currentPlayer,
      });
    } else {
      applyWanderingAndDraw(validTargets[0].index);
    }

    return true;
  },

  /**
   * C0000570: マリスの誘導
   * 【彷徨い】状態の相手モンスター1体を選択する。そのコントロールを得る。
   * ただし、自分の場に《ソウルカース》モンスターが1体以上いなければ発動できない。
   */
  C0000570: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField, currentPlayer } = getPlayerContext(context);

    // 自分の場にソウルカースモンスターがいるかチェック
    const hasSoulCurse = myField.some((m: MonsterInstance | null) => {
      if (!m) return false;
      return m.category?.includes('ソウルカース');
    });

    if (!hasSoulCurse) {
      addLog('マリスの誘導: 自分の場に《ソウルカース》モンスターがいません', 'info');
      return false;
    }

    // 自分の場に空きがあるかチェック
    const emptySlotIndex = myField.findIndex((slot: MonsterInstance | null) => slot === null);
    if (emptySlotIndex === -1) {
      addLog('マリスの誘導: 自分の場に空きがありません', 'info');
      return false;
    }

    // 【彷徨い】状態の相手モンスターを抽出
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter((item): item is { monster: MonsterInstance; index: number } => {
        if (!item.monster) return false;
        // 彷徨い状態かチェック
        const hasWandering = item.monster.statusEffects?.some(
          (s: { type: string }) => s.type === STATUS_EFFECT_TYPES.WANDERING
        );
        return !!hasWandering;
      });

    if (validTargets.length === 0) {
      addLog('マリスの誘導: 【彷徨い】状態の相手モンスターがいません', 'info');
      return false;
    }

    // コントロールを奪う関数
    const stealControl = (targetIndex: number) => {
      const targetMonster = opponentField[targetIndex];
      if (!targetMonster) return;

      // 相手の場から除去
      setOpponentField(prev => {
        const newField = [...prev];
        newField[targetIndex] = null;
        return newField;
      });

      // 自分の場に追加（彷徨い状態を解除してコントロール奪取）
      setMyField(prev => {
        const newField = [...prev];
        const slot = newField.findIndex(s => s === null);
        if (slot !== -1) {
          // 彷徨い状態を解除
          const clearedStatusEffects = (targetMonster.statusEffects || []).filter(
            (s: { type: string }) => s.type !== STATUS_EFFECT_TYPES.WANDERING
          );
          newField[slot] = {
            ...targetMonster,
            owner: currentPlayer as PlayerNumber,
            canAttack: false, // 召喚酔い
            statusEffects: clearedStatusEffects,
            // 元の攻撃力に戻す
            currentAttack: targetMonster.attack || 0,
          };
        }
        return newField;
      });

      addLog(`マリスの誘導: ${targetMonster.name}のコントロールを奪った！`, 'info');
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      stealControl(validTargets[0].index);
      return true;
    }

    // 複数の場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: 'コントロールを奪う【彷徨い】モンスターを選択',
        targetPlayer: 'opponent',
        validTargets: validTargets.map(t => t.index),
        callback: (selectedIndex: number) => {
          stealControl(selectedIndex);
        },
        selectionOwner: currentPlayer,
      });
    } else {
      stealControl(validTargets[0].index);
    }

    return true;
  },

  // ========================================
  // 彷徨い関連フェイズカード
  // ========================================

  /**
   * C0000571: 彷徨いの儀式（フェイズカード）
   * 初期効果: コスト4以下の相手モンスター1体を【彷徨い】状態にする。
   * 1枚重ね: コスト5以下の相手モンスター1体を【彷徨い】状態にする。
   * 2枚重ね: 相手モンスター1体を【彷徨い】状態にする。
   * 3枚重ね: 相手の【彷徨い】状態のモンスターを全て破壊し、破壊した数×600ダメージ。墓地送り。
   */
  C0000571: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, currentPlayer, setOpponentLife } = getPlayerContext(context);

    // 段階を判定
    const is3Stack = skillText.includes('3枚重ね');
    const is2Stack = skillText.includes('2枚重ね');
    const is1Stack = skillText.includes('1枚重ね');
    const isInitial = skillText.includes('初期効果');

    // 3枚重ね: 彷徨い状態の相手を全破壊 + ダメージ
    if (is3Stack) {
      const wanderingMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter((item): item is { monster: MonsterInstance; index: number } => {
          if (!item.monster) return false;
          return item.monster.statusEffects?.some(
            (s: { type: string }) => s.type === STATUS_EFFECT_TYPES.WANDERING
          ) ?? false;
        });

      if (wanderingMonsters.length === 0) {
        addLog('彷徨いの儀式【最終効果】: 【彷徨い】状態の相手モンスターがいません', 'info');
        return true;
      }

      const destroyedCount = wanderingMonsters.length;
      const damage = destroyedCount * 600;

      // 全破壊
      setOpponentField(prev => {
        const newField = [...prev];
        wanderingMonsters.forEach(({ index }) => {
          const monster = newField[index];
          if (monster) {
            addLog(`彷徨いの儀式: ${monster.name}を破壊！`, 'damage');
            newField[index] = null;
          }
        });
        return newField;
      });

      // ダメージを与える
      setOpponentLife((prev: number) => Math.max(0, prev - damage));
      addLog(`彷徨いの儀式【最終効果】: ${destroyedCount}体破壊！${damage}ダメージ！`, 'damage');

      return true;
    }

    // 初期〜2枚重ね: 条件付きで彷徨い付与
    let maxCost = Infinity;
    let stageName = '';

    if (isInitial) {
      maxCost = 4;
      stageName = '初期効果';
    } else if (is1Stack) {
      maxCost = 5;
      stageName = '1枚重ね';
    } else if (is2Stack) {
      maxCost = Infinity;
      stageName = '2枚重ね';
    } else {
      // 不明な段階
      return false;
    }

    // 条件に合う相手モンスターを抽出（飛行持ちは除外）
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter((item): item is { monster: MonsterInstance; index: number } => {
        if (!item.monster) return false;
        // コスト条件
        if ((item.monster.cost || 0) > maxCost) return false;
        // 飛行持ちは彷徨いにならない
        if (item.monster.keyword?.includes('飛行')) return false;
        return true;
      });

    if (validTargets.length === 0) {
      if (maxCost < Infinity) {
        addLog(`彷徨いの儀式【${stageName}】: コスト${maxCost}以下の対象モンスターがいません`, 'info');
      } else {
        addLog(`彷徨いの儀式【${stageName}】: 対象モンスターがいません`, 'info');
      }
      return true;
    }

    // 状態異常を適用する関数
    const applyWandering = (targetIndex: number) => {
      setOpponentField(prev => prev.map((m, idx) => {
        if (idx === targetIndex && m) {
          const result = statusEffectEngine.applyStatus(m, STATUS_EFFECT_TYPES.WANDERING, {
            duration: 3,
            source: 'C0000571',
            sourceName: '彷徨いの儀式',
          });
          if (result.success) {
            addLog(`彷徨いの儀式【${stageName}】: ${m.name}を【彷徨い】状態にした！`, 'damage');
          } else if (result.reason) {
            addLog(`彷徨いの儀式: ${m.name} - ${result.reason}`, 'info');
          }
          return { ...m };
        }
        return m;
      }));
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      applyWandering(validTargets[0].index);
      return true;
    }

    // 複数の場合は選択UI
    if (setPendingMonsterTarget) {
      const costText = maxCost < Infinity ? `コスト${maxCost}以下の` : '';
      setPendingMonsterTarget({
        message: `【彷徨い】にする${costText}モンスターを選択`,
        targetPlayer: 'opponent',
        validTargets: validTargets.map(t => t.index),
        callback: (selectedIndex: number) => {
          applyWandering(selectedIndex);
        },
        selectionOwner: currentPlayer,
      });
    } else {
      applyWandering(validTargets[0].index);
    }

    return true;
  },

  /**
   * C0000560: 遮光カーテン
   * 相手フィールドの光属性モンスター1体を手札に戻す。
   */
  C0000560: (skillText: string, context) => {
    const { addLog, setPendingMonsterTarget, currentPlayer } = context;
    const { opponentField, setOpponentField, setOpponentHand } = getPlayerContext(context);

    // 相手フィールドの光属性モンスターを探す
    const lightMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter((item): item is { monster: MonsterInstance; index: number } => {
        return item.monster !== null && item.monster.attribute === '光';
      });

    if (lightMonsters.length === 0) {
      addLog('遮光カーテン: 相手フィールドに光属性モンスターがいません', 'info');
      return false;
    }

    // 手札に戻す処理
    const returnToHand = (targetIndex: number) => {
      const target = opponentField[targetIndex];
      if (!target) return;

      // フィールドから除去
      setOpponentField(prev => {
        const newField = [...prev];
        newField[targetIndex] = null;
        return newField;
      });

      // 手札に戻す（カード状態にリセット）
      const cardData = {
        id: target.id,
        uniqueId: `${target.id}_returned_${Date.now()}`,
        name: target.name,
        attribute: target.attribute,
        cost: target.cost,
        type: target.type,
        attack: target.attack,
        hp: target.hp,
        category: target.category,
        effect: target.effect,
        keyword: target.keyword,
      };
      setOpponentHand(prev => [...prev, cardData as CardWithId]);
      addLog(`遮光カーテン: ${target.name}を相手の手札に戻した！`, 'info');
    };

    // 1体のみの場合は自動選択
    if (lightMonsters.length === 1) {
      returnToHand(lightMonsters[0].index);
      return true;
    }

    // 複数の場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '手札に戻す光属性モンスターを選択',
        targetPlayer: 'opponent',
        validTargets: lightMonsters.map(t => t.index),
        callback: (selectedIndex: number) => {
          returnToHand(selectedIndex);
        },
        selectionOwner: currentPlayer,
      });
    } else {
      returnToHand(lightMonsters[0].index);
    }

    return true;
  },
};
